import { DataTableRequestFilterDateTimeAdapter } from '~/components/acurity-table/filters/data-table-request-filter-date-time-adapter';
import { CodeType as CodeTypeEnum } from '~/models/form-model';
import { Tokens } from '@okta/okta-auth-js';
import { EventAggregator } from 'aurelia-event-aggregator';
import {
	autoinject,
	bindable,
	bindingMode,
	computedFrom,
} from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { activationStrategy, Router } from 'aurelia-router';
import { StateHistory, Store } from 'aurelia-store';
import { BindingSignaler } from 'aurelia-templating-resources';
import httpStatus from 'http-status';
import { debounce, result } from 'lodash-es';
import PerfectScrollbar from 'perfect-scrollbar';

import 'jquery';

import 'datatables.net';
import 'datatables.net-bs4';
import 'datatables.net-colreorder';
import 'datatables.net-select';
import '~/components/acurity-table/data-tables-go-to-page-plugin';
import '~/components/acurity-table/data-tables-reverse-order-plugin';

import moment from 'moment';

import { APIParameters } from '~/api/generic-api';
import { AppState } from '~/app-store';
import { BaseViewModel } from '~/base-view-model';
import { ContextMenuItem } from '~/components/context-menu-list/context-menu-list';
import { ContextMenuPosition } from '~/components/context-menu/context-menu';
import {
	FilterOption,
	filterOptions,
	FilterType,
	getFilterOption,
	getFilterOptionsForDataType,
} from '~/components/filter/filter-options';
import { Notification } from '~/components/notification/notification';
import { DEBOUNCE_DURATION, ShortcutKey } from '~/constants';
import { DateFormatValueConverter } from '~/converters/date-format';
import {
	ExcelExportDialogModel,
	ExcelExportDialogModelDefaults,
} from '~/forms/excel-export-dialog';
import { LoginConstants } from '~/login/login-constants';
import { OKTA_TOKEN_STORAGE, OktaLogin } from '~/login/okta-login';
import {
	CodeAndDescription,
	DescriptionForCode,
} from '~/models/code-and-description';
import {
	ColumnDefinition,
	ColumnDefinitionMap,
	HiddenTarget,
} from '~/models/column-definition';
import { DataType, DataTypeEnum } from '~/models/data-type';
import { fieldTypes } from '~/models/field-type';
import { createFilterModel, FilterModel } from '~/models/filter-model';
import { SettingsState } from '~/settings-state/settings-state';
import { Clipboard } from '~/utils/clipboard';
import { Converters, ConverterTypes } from '~/utils/converters';
import { DateTimeProperties } from '~/utils/datetime/date-time-properties';
import { Export, ExportColumn } from '~/utils/export';
import { FetchUtils } from '~/utils/fetch-utils';
import FormModelUtils from '~/utils/form-model-utils';
import { Key } from '~/utils/key';
import { KeyboardShortcut } from '~/utils/keyboard-shortcut';
import { getItem } from '~/utils/local-storage';
import { Logger } from '~/utils/logger';
import { StringUtils } from '~/utils/string-utils';
import { LockUntilReady, ObjectUtils } from '~/utils/utils';
import { WarningMessages } from '~/models/warning-messages';

/**
 * Basic table column defintion
 */
export type TableColumnDefinition<T extends string = string> =
	BackendTableColumnDefinition<T> & {
		// NOTE: the property doesn't work when defining in {@link FrontendTableColumnDefinitionOverrides}
		width?: string;
		// Indicates if the nature of this field is local despite its name do not start with "__"
		// (they are known to the backend).
		localField?: boolean;
		// Fields that need to be fetched to produce the value of this field.
		dependsOn?: T[];
		// To enable tool tip for the column text
		enableToolTip?: boolean;
		// For any customized to the date/timestamp datatype
		dateTimeProperties?: DateTimeProperties;
	};

export type FrontendTableColumnDefinitionOverrides<T extends string = string> =
	{ [P in T]?: Exclude<Partial<TableColumnDefinition<T>>, 'name'> };

/**
 * This should correspond to the Java class  au.com.finsyn.acuritywebserver.core.datatables.mapping.TableColumnDefinition
 */
export type BackendTableColumnDefinition<T extends string = string> =
	ColumnDefinition<T> &
		TableColumnSlotDefinition & {
			isDisableColumnDescription?: boolean;
			unsortable?: boolean;
		} & {
			codeAndDescriptionKey?: string;
			codeType?: CodeTypeEnum;
		};

interface TableColumnSlotDefinition {
	slot?: string;
}

type RowListener = (rowData) => void;

interface NormalPageParams {
	dataSet: object[];
	defaultColumnInfo: ColumnDefinitionMap;
	iconMap?: any;
}

// todo: Jake: 2018-11-09
// Improve typing on the selection variable
// interface DataTableRowElement extends HTMLElement {
// 	_DT_RowIndex?: any
// }

interface Selection {
	row: any; // JQuery<DataTableRowElement>
	page: number;
}

/**
 * This should correspond to the Java class au.com.finsyn.acuritywebserver.core.datatables.mapping.Column
 */
export interface DataTablesRequestColumn {
	data: string;
	orderable: boolean;
	search: DataTablesRequestColumnSearch;
	searchable: boolean;
	name?: string;
	ignore?: boolean;
}

/**
 * This should correspond to the Java class au.com.finsyn.acuritywebserver.core.datatables.mapping.Search
 */
export interface DataTablesRequestColumnSearch {
	value: string;
	regex: false;
}

/**
 * This should correspond to the Java class au.com.finsyn.acuritywebserver.core.datatables.mapping.Order
 */
export interface DataTablesRequestOrder {
	column: number;
	dir: 'desc' | 'asc'; // ie. "desc"
}

/**
 * This should correspond to the Java class au.com.finsyn.acuritywebserver.core.datatables.mapping.Filter
 */
export interface DataTableRequestFilter {
	data: string; // column name
	value?: string;
	upperValue?: string;
	// NOTE: Jake: 2019-01-16
	// NOTE: York: 2019-1-22 - WEBUI-1424
	// - Frontend FilterModel.operation is 'FilterOption' object
	// - But API request filter.operation is a  FilterType string
	operation: FilterType;
	not: boolean;
	ignoreCase: boolean;
	andJoin?: boolean;
}

/**
 * -1 to indicate that all records from the start point on should be returned.
 */
export const ALL_DATA_LENGTH = -1;

/**
 * This should correspond to the Java class au.com.finsyn.acuritywebserver.core.datatables.mapping.DataTablesInput
 */
export interface DataTablesRequest {
	columns: DataTablesRequestColumn[];
	filters: DataTableRequestFilter[];
	order: DataTablesRequestOrder[];
	length: number;
	reverseOrder: boolean;
	mode: 'all' | 'count_only' | 'data_only';
}

export interface DataTablesResponse extends WarningMessages {
	recordsTotal: number;
	recordsTotalExceedsLimit: boolean;
	data: any[];
	descriptionMap: { [fieldName: string]: DescriptionDefinition };
	readOnlyRecords: number[];
	columnLabelMap: { [columnName: string]: string };
	error?: string;
	/**
	 * tableConfig might be provided by the backend, in which case we should use
	 * the columns suggested.
	 */
	tableConfig?: BackendAcurityTableConfig;
}

export interface DescriptionDefinition {
	codeMap: Map<string, DescriptionForCode>;
	codeFields?: CodeFieldDefinition[];
}

interface CodeFieldDefinition {
	name: string;
	length: number;
}

interface SecondarySortField {
	primary: string;
	secondary: string;
}

/**
 * AcurityTableConfig are the configuration values that can be given to a table.
 */
export interface AcurityTableConfig<T extends string = string>
	extends BackendAcurityTableConfig<T> {
	/**
	 * callback function to execute after table has filtered. This function allows you to modify DataTableRequestFilter
	 * array. This allows new filter is added or removed from query data and keep the filter UI (from tableColumnInfo)
	 * unchanged. Make sure you understand what you are doing.
	 */
	onFilter?: (filters: DataTableRequestFilter[]) => void;
	/**
	 * callback function to execute after table has been rendered
	 */
	onDraw?: (
		viewModel: AcurityTable,
		$table: JQuery<HTMLTableElement>,
	) => void;
	/**
	 * Footer display callback function.
	 */
	footerCallback?: DataTables.FunctionFooterCallback;
	/**
	 * @deprecated Use 1 array item in "multiColumnOrder" instead. Ideally you should move all config settings
	 * to the backend where possible.
	 */
	defaultSortField: T | ''; // = '';
	/**
	 * The default sort direction of table data.
	 * 'desc' was chosen as the default as that was what most forms use by default.
	 *
	 * @deprecated Use 1 item in "multiColumnOrder" instead. DESC is the default. Ideally you should move all config settings
	 * to the backend where possible.
	 */
	defaultSortDirection: 'asc' | 'desc'; // = 'desc';
	/**
	 * @deprecated Only "ato-interfaces" should remain using this until its updated to use "multiColumnOrder" + tested to make sure
	 * nothing was broken.
	 */
	secondarySortFields?: SecondarySortField[];
	/**
	 * Select the record if the table contains only one record.
	 * This will automatically open the record in a slideout.
	 */
	selectIfOnlyOne?: boolean;

	/**
	 * Used to set ajax table or normal table.
	 * If not set, acurity-table has a default value for bindable property isAjaxTable which is true
	 */
	isAjaxTable?: boolean;
	/**
	 * Used to flag whether setting backend config through ConfigReady event e.g. Member-beneficiaries
	 */
	loadConfigFromConfigReadyEvent?: boolean;
	/**
	 * Should set this to true if loadTableOnPageLoad is falsy.
	 */
	loadTableOnDataReady?: boolean;
	/**
	 * Should set this as a data source if isAjaxTable is falsy.
	 */
	tableDataSet?: AcurityTableRecord[];
	/**
	 * If set to true, acurity-table is loading and spinner displays.
	 */
	isLoading?: boolean;
	/**
	 * Set this to override properties of table columns that have been retrieved from the back-end. This is necessary
	 * for properties that are properly the domain of the front end only, such as hiding columns and custom rendering of
	 * columns.
	 */
	frontEndTableColumnsOverrides?: FrontendTableColumnDefinitionOverrides<T>;
}

/**
 * This should correspond to the Java class au.com.finsyn.acuritywebserver.core.datatables.mapping.AcurityTableConfig
 */
export interface BackendAcurityTableConfig<T extends string = string> {
	tableColumns: Array<TableColumnDefinition<T>>;
	multiColumnOrder: Array<ColumnOrder<T>>; // = [];
	rowIdKey: T | ''; // = '';
	/**
	 * The name of the column that means a record is deleted.
	 * - This is used to highlight a field in the acurity table with red.
	 * - This will also add the deleted field to the filter panel. (WEBUI-2486)
	 */
	deletedHighlightField: T | ''; // = '';
	/**
	 * The name of the column that means a record is cancelled.
	 * This is used to highlight a field in the acurity table with red and is mostly
	 * used for transaction forms.
	 */
	canceledHighlightField?: T;
	/**
	 * These columns are defined on the front end only because they do not correspond
	 * to any database columns and hold logic for rendering the columns on the UI
	 */
	frontEndTableColumns?: Array<TableColumnDefinition<T>>;
}

export interface ColumnOrder<T extends string = string> {
	name: T;
	direction: 'desc' | 'asc';
}

export enum RecordIdentifier {
	FIRST,
	LAST,
	NEXT,
	PREVIOUS,
}

export enum PageIdentifier {
	FIRST = 'first',
	LAST = 'last',
	NEXT = 'next',
	PREVIOUS = 'previous',
}

interface CodeType {
	code: string; // ie. 'P'
	description: string; // ie. 'Preserved'
}

export interface AcurityTableRecord {
	[fieldName: string]: any;

	DT_RowId?: number | string;
	__contextMenu?: string; // Raw HTML
}

@autoinject
export class AcurityTable extends BaseViewModel {
	/**
	 * AcurityTable events
	 */
	public static readonly Events = {
		InitContextMenuEvents: 'Table.InitContextMenuEvents',
		// Publish ConfigReady event when table config is loaded from backend.
		// An example can be seen in member-beneficiaries form.
		ConfigReady: 'Table.ConfigReady',
		Attached: 'Table.Attached',
		ShowContextMenu: 'Table.ShowContextMenu',
		HideContextMenu: 'Table.HideContextMenu',
		AjaxDataReady: 'Table.AjaxDataReady',
	};

	// Table ID Keys
	@bindable public tableId: string; // mandatory
	@bindable public tableName: string; // optional
	@bindable public rowIdKey: string; // optional

	// Datatable Properties
	@bindable public colReorder: boolean = true; // optional
	@bindable({
		defaultBindingMode: bindingMode.twoWay,
		changeHandler: 'tableDataSetChanged',
	})
	public tableDataSet: AcurityTableRecord[];

	@bindable public sortable: boolean = true; // optional
	@bindable({
		changeHandler: 'pageLengthChanged',
	})
	public pageLength: number = 0; // optional
	@bindable public paging: boolean = true; // optional, set to false when presenting data with fewer than 10 records.
	@bindable public processing: boolean = false; // optional
	@bindable public searching: boolean = false; // optional

	@bindable public loadConfigFromConfigReadyEvent: boolean = false; // optional, eg. in member-beneficiaries

	@bindable public readonly isAjaxTable: boolean = true; // optional

	// Column Sort Ordering
	@bindable public multiColumnOrder: ColumnOrder[]; // optional
	@bindable public defaultSortField: string; // optional. if not set, table is sorted across all visible columns
	@bindable public defaultSortDirection: 'desc' | 'asc' = 'asc'; // optional
	@bindable public secondarySortFields?: SecondarySortField[]; // optional, used for columns that reference two fields

	// Context Menu
	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	private contextMenuItemConfigs: ContextMenuItem[];

	/**
	 * true when data is being fetched for a row
	 */
	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	@bindable
	private isRowLoading: boolean = false;

	/**
	 * This is an optional attribute used as a hint while rendering the context menu.
	 */
	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	@bindable
	private contextMenuMaxItemsCount: number;

	@bindable public contextMenu: boolean = false;

	@bindable public excludeClone: boolean = false;

	private readonly exportResultSize: number = 10000;

	// Styling
	@bindable public width: string = 'auto'; // optional

	public recordsTotal: number = null;
	private recordsTotalExceedsLimit: boolean = null;
	private static COUNT_UNAVAILABLE = -1;

	/**
	 * isLoading will show a spinner if true, disable the spinner if false.
	 */
	@bindable({
		defaultBindingMode: bindingMode.toView,
		changeHandler: 'isLoadingChanged',
	})
	private isLoading: boolean = false;

	/**
	 * If a field has the value 'Y', then highlight the row.
	 * ie. A *_Deleted field will either have a 'N' or 'Y' value.
	 */
	@bindable public deletedHighlightField: string = ''; // optional

	/**
	 * If a field has the value 'C', then highlight the row.
	 * ie. A TransactionPostedEnum can be 'Y', 'N', 'C' or 'P'.
	 */
	@bindable public canceledHighlightField?: string = ''; // optional

	@bindable public tableHover: boolean = true; // optional
	@bindable public iconMap;

	/**
	 * Table row height
	 * __ALWAYS__ needs to be the same as `$table-body-row-height` in `_datatables.css`
	 */
	@bindable public rowHeight: number = 32;

	// Load/Refresh table when tableDataSet provided
	@bindable({ defaultBindingMode: bindingMode.oneWay })
	public loadTableOnDataReady: boolean = undefined;
	@bindable({ defaultBindingMode: bindingMode.oneWay })
	public tableLoadingMessage: string;
	@bindable public loadTableOnPageLoad: NormalPageParams;

	// must provide at least one of these
	@bindable({
		defaultBindingMode: bindingMode.toView,
		changeHandler: 'tableColumnsChanged',
	})
	public tableColumns: TableColumnDefinition[];
	@bindable public frontEndTableColumns?: TableColumnDefinition[];
	@bindable public defaultColumnInfo: ColumnDefinitionMap;
	@bindable
	public frontEndTableColumnsOverrides?: FrontendTableColumnDefinitionOverrides;
	/**
	 * Map of dataTypes derived from `fieldConfig` customisation. Allows user to set dataType on a
	 * field which is carried over to setting the same dataType on the corresponding table column
	 */
	public fieldConfigDataTypeMap?: Map<string, DataType>;

	/**
	 * Setting this will override the default calculation for the available filter options for the set of fields
	 * specified by the index field names. The filter options are the types of filtering that can be selected from.
	 * This is currently used by the AAz_Table_Name field on the record-authorisations form. It should be used for all
	 * fields where the filter value is selected by an <a-search> field.
	 */
	@bindable public customFilterOptions: {
		[fieldName: string]: FilterOption[];
	};

	// References
	private rowId = 1;
	private currentSelection: Selection = { row: -1, page: -1 };
	private jqueryTableReference: JQuery<HTMLTableElement>;
	private ajaxData?: (data: DataTablesRequest) => void;
	// selected <tr> element id, which may contain special characters and starts with digit.
	// Beware that it should be escaped when query, e.g, document.querySelector('#' + $.escapeSelector(selectedObjId))
	// see https://datatables.net/reference/option/rowId
	public selectedObjId: string;
	private ajaxURL: string;
	private useAdvancedPagination: boolean;
	private selectedColumnIndex: number;

	// Columns
	private tableColumnSetting: DataTables.ColumnSettings[];
	public tableColumnInfo: TableColumnDefinition[] = [];

	// WEBUI-4247: Tracks the order of tableColumnSetting when ColReorder extension is triggered
	private tableColumnOrder: number[];

	private maxResultSize: number = 100000;

	// Listeners
	private clickListener: RowListener;

	// Locks
	private readonly tableReady = new LockUntilReady();
	private readonly ajaxLoaded = new LockUntilReady();

	// I18N
	private datatablesTranslations;

	private ignoreAjaxOnceNextCall: boolean = false;
	private ajaxReturnedFilter: boolean = false;

	/**
	 * autoWidth will make the table have an automatic column width
	 *
	 * @see https://datatables.net/reference/option/autoWidth
	 */
	@bindable private autoWidth = true;

	@bindable private footerCallback?: DataTables.FunctionFooterCallback;

	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	private readonly onFilter?: (filters: DataTableRequestFilter[]) => void;

	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private readonly onDraw?: (
		viewModel: AcurityTable,
		$table: JQuery<HTMLTableElement>,
	) => void;

	@bindable({
		defaultBindingMode: bindingMode.oneTime,
	})
	private readonly selectIfOnlyOne?: boolean;

	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	private readonly normalDescriptionMap?: {
		[fieldName: string]: DescriptionDefinition;
	};

	/**
	 * A collection of descriptions that provide a more detailed explaination for *some* field types.
	 * For AJAX tables, this is derived from the DataTablesResponse but for Normal tables, we may
	 * explicitly bind this map via normalDescriptionMap in order to set this.
	 */
	private descriptionMap?: { [fieldName: string]: DescriptionDefinition };

	/**
	 * True if a valid tableColumnConfig is declared in the JSON customisation as we do not want to
	 * override a user customisation with the columnLabelMap from the Configuration table
	 */
	public customColumns?: boolean;

	/**
	 * A list of ids for records that are read only within a table of records that are editable by default.
	 */
	private _readOnlyRecords?: number[];

	/**
	 * Dynamically calculate page length based on height of the table.
	 */
	@bindable private fillTable: boolean = false;

	/**
	 * Selector for the context menu button
	 */
	private readonly contextMenuBtnCls: string = 'a-table__context-menu-btn';

	/**
	 * previousDataTablesResponse holds the last response we got from the DataTables API.
	 * We use this so we can re-create a table without triggering an AJAX request, which we
	 * need to do if we want to receive the table columns from the backend *then* apply them.
	 */
	private previousDataTablesResponse: string | undefined;

	/**
	 * The handle and wrapper debounce function around onResize().
	 */
	private resizeHandle?: (e: UIEvent) => void;

	/**
	 * Perfect scroll bar instance
	 */
	private perfectScroll?: PerfectScrollbar;

	private tableEl?: HTMLTableElement;

	/**
	 * Holds the current state of component.
	 * Its used to prevent datatable from continuing the initialization of detached component.
	 */
	private isDetached: boolean = false;

	private contextMenuPos?: ContextMenuPosition;

	/**
	 * Determines whether we want to destroy and recreate table in order to apply the
	 * tableConfig provided by the DataTablesResponse. Only needs to be done once.
	 */
	private recreateTableFromResponseConfig: boolean = true;

	constructor(
		protected router: Router,
		protected eventAggregator: EventAggregator,
		protected i18n: I18N,
		protected store: Store<StateHistory<AppState>>,
		private element: Element,
		private bindingSignaler: BindingSignaler,
		private readonly settingsState: SettingsState,
	) {
		super();
		this.setupTranslation();
	}

	public determineActivationStrategy() {
		return activationStrategy.replace;
	}

	protected attached() {
		this.isDetached = false;
		if (
			this.jqueryTableReference &&
			this.jqueryTableReference.length === 1
		) {
			// Ignore if already initialized.
			return;
		}
		this.jqueryTableReference = $(this.tableEl);
		if (
			!this.jqueryTableReference ||
			this.jqueryTableReference.length === 0
		) {
			throw new Error('Cannot find <table> element.');
		}

		// tfoot has to be added dynamically for footercallback to work
		// https://datatables.net/forums/discussion/27829/add-table-footer-with-javascript-only
		if (this.footerCallback && this.tableColumns) {
			this.jqueryTableReference.append(
				'<tfoot><tr>' +
					'<td></td>'.repeat(this.tableColumns.length) +
					'</tr></tfoot>',
			);
		}

		this.jqueryTableReference.on('init.dt', () => this.onDataTableInit());
		this.jqueryTableReference.on(
			'processing.dt',
			(e, settings, processing) =>
				this.onDataTableProcessing(e, settings, processing),
		);
		this.jqueryTableReference.on('draw.dt', this.onDataTableDraw);
		this.jqueryTableReference.on('xhr.dt', (e, settings, json, xhr) =>
			this.onDataTableXHR(e, settings, json, xhr),
		);

		// NOTE: Jake: 2019-02-06
		// We do this so we can control the error handling ourselves.
		// https://datatables.net/reference/event/error
		$.fn.dataTable.ext.errMode = 'none';
		this.jqueryTableReference.on('error.dt', this.onDataTableError);

		this.jqueryTableReference.on(
			'column-reorder.dt',
			(
				e: any,
				settings: DataTables.Settings,
				details: DataTables.ColReorderDetails,
			) => {
				if (details?.from && details?.to) {
					this.tableColumnOrder.splice(
						details.to,
						0,
						this.tableColumnOrder.splice(details.from, 1)[0],
					);
				}
			},
		);

		if (this.loadTableOnDataReady) {
			this.toggleSpinner(
				this.tableDataSet === null || this.tableDataSet === undefined,
			);
		}

		this.initColumnHeaderIcons();

		this.bindingSignaler.signal('acurityTableAttached');
		this.publishEvent(AcurityTable.Events.Attached, { table: this });

		if (!this.resizeHandle) {
			this.resizeHandle = debounce(
				(e) => this.onResize(e),
				DEBOUNCE_DURATION,
			);
			window.addEventListener('resize', this.resizeHandle);
		}
		if (this.ajaxURL) {
			// NOTE(Ben):
			// this.ajaxURL will never be initialized with any value unless call this.initAjaxTable or this.setAjaxURL. Currently classic-form will call initAjaxTable
			// when received AcurityTable.Attached event so that this clause can be triggered, but it would be duplicate with
			// the next line `this.initAjaxTable(this.ajaxURL)` insided of this clause. Tested either to delete the next line call or replcace this.initAjaxTable with this.setAjaxURL in classic-form can still
			// make the table work.  Maybe we can refactor this?
			this.initAjaxTable(this.ajaxURL);
		} else if (this.loadTableOnPageLoad) {
			// init normal table with no pagination from declarative once page is loaded
			this.initNormalTable(
				this.loadTableOnPageLoad.dataSet
					? this.loadTableOnPageLoad.dataSet
					: this.tableDataSet,
				this.loadTableOnPageLoad.defaultColumnInfo,
				this.loadTableOnPageLoad.iconMap,
			);
		} else if (this.loadConfigFromConfigReadyEvent) {
			// NOTE(York): 2021-07-05
			// if table config is loading from backend. We should delay normal table
			// initialisation until table config is fetched using ConfigReady Event.
			this.subscribeToEventOnce(
				AcurityTable.Events.ConfigReady,
				(config: BackendAcurityTableConfig) => {
					this.rowIdKey = config.rowIdKey;
					if (this.tableColumns?.length === 0) {
						this.tableColumns = this.convertTableColumnsFromConfig(
							config.tableColumns,
						);
					}
					this.multiColumnOrder = config.multiColumnOrder;
					this.deletedHighlightField = config.deletedHighlightField;
					this.initTableColumnInfoAndTableColumnSettings();
					this.refreshNormalTable(
						this.tableDataSet,
						this.normalDescriptionMap,
					);
				},
			);
		} else {
			// NOTE(Jake): 2018-10-12
			// This codepath is used by audit-dialog.
			this.initTableColumnInfoAndTableColumnSettings();
			this.refreshNormalTable(
				this.tableDataSet,
				this.normalDescriptionMap,
			);
		}
	}

	private convertTableColumnsFromConfig(
		tableColumns: TableColumnDefinition[],
		isAjax: boolean = false,
	) {
		this.ajaxReturnedFilter = false;
		return tableColumns.map((column) => {
			const columnUntyped = column as any;
			if (columnUntyped?.filter?.operation) {
				columnUntyped.filter.operation = getFilterOption(
					columnUntyped.filter.operation,
				);
			}
			if (
				isAjax &&
				(columnUntyped?.filter?.value ||
					columnUntyped?.filter?.upperValue)
			) {
				this.ajaxReturnedFilter = true;
			}
			return columnUntyped;
		});
	}

	protected detached() {
		this.isDetached = true;
		this.destroyDataTable();

		this.jqueryTableReference.off('init.dt');
		this.jqueryTableReference.off('processing.dt');
		this.jqueryTableReference.off('draw.dt');
		this.jqueryTableReference.off('xhr.dt');
		window.removeEventListener('resize', this.resizeHandle);
	}

	private onResize(e: UIEvent): void {
		// NOTE(Jake): 2018-10-03
		// Currently AJAX tables will re-query if you change the page length,
		// so for now, only resize the table if we have all the data already. (ie. not ajax)
		if (!this.ajaxURL) {
			// NOTE(Jake): 2018-10-03
			// We trigger this as the page length is determined by the
			// amount of items we can fit on the screen.
			this.calculatePageLengthForFillTable();
		}

		if (this.perfectScroll) {
			// NOTE(Jake): 2018-10-03 - WEBUI-1025
			// We want scrollbars to appear on the perfect scrollbar when
			// you resize the screen and *after* the number of items rendered
			// in the table is updated.
			// https://github.com/utatti/perfect-scrollbar/issues/221
			this.perfectScroll.update();
		}
	}

	/**
	 * Setup Translation
	 */
	private setupTranslation() {
		const translateValues = (obj: any) => {
			if (typeof obj === 'object') {
				for (const propertyName of Object.keys(obj)) {
					obj[propertyName] = translateValues(obj[propertyName]);
				}
			} else if (typeof obj === 'string') {
				obj = this.i18n.tr(obj);
			}
			return obj;
		};
		this.datatablesTranslations = translateValues({
			decimal: 'datatables.decimal',
			emptyTable: 'datatables.emptyTable',
			info: 'datatables.info',
			infoEmpty: 'datatables.infoEmpty',
			infoFiltered: 'datatables.infoFiltered',
			infoPostFix: 'datatables.infoPostFix',
			thousands: 'datatables.thousands',
			lengthMenu: 'datatables.lengthMenu',
			loadingRecords: 'datatables.loadingRecords',
			processing: 'datatables.processing',
			search: 'datatables.search',
			zeroRecords: 'datatables.zeroRecords',
			paginate: {
				first: 'datatables.paginate.first',
				last: 'datatables.paginate.last',
				next: 'datatables.paginate.next',
				previous: 'datatables.paginate.previous',
			},
			aria: {
				sortAscending: 'datatables.aria.sortAscending',
				sortDescending: 'datatables.aria.sortDescending',
			},
		});
	}

	private isLoadingChanged(newValue: boolean, oldValue: boolean): void {
		this.toggleSpinner(newValue);
	}

	/**
	 * Aurelia activates this whenever tableDataSet variable is changed
	 */
	private tableDataSetChanged(newValue, oldValue) {
		if (!this.jqueryTableReference) {
			// Skip, if we don't have "jqueryTableReference" then we
			// haven't executed "attached()" yet which means we haven't
			// rendered a DataTable yet.
			return;
		}
		if (!this.loadTableOnDataReady) {
			return;
		}
		const dataTable = this.getDataTable();
		if (dataTable) {
			// Refresh existing DataTable with new data
			dataTable.clear();
			// WEBUI-4332: `descriptionMap` is normally undefined for normal tables
			// If it is, try using the `normalDescriptionMap` if it is bound to the table
			this.updateRowDataWithMoreInformation(
				newValue,
				this.descriptionMap || this.normalDescriptionMap,
			);
			if (newValue) {
				dataTable.rows.add(newValue);
			}
			// WEBUI-2646:  the ordering and search will be recalculated and the rows redrawn in their new positions.
			// The paging will not be reset.
			dataTable.draw(false);
		} else {
			Logger.warning(
				'DataTable is not created yet when tableDataSetChanged is triggered because tableDataSet is set.',
			);
			// Note: York - WEBUI-4094 - See commit history that init table code is removed: `this.initNormalTable(newValue, {}, {});`
			// If dataTable is not yet created, there is no need to initialize it.
			// See three branches of normal table initialization in `attached` method. They all do these and make sure the latest tableDataSet is used.
			// - initNormalTableData
			// - updateRowDataWithMoreInformation
			// - initTableColumnInfoAndTableColumnSettings
			// - initNormalTableOptions <- where this.tableDataSet is used
		}

		this.toggleSpinner(newValue === null || newValue === undefined);
	}

	/**
	 * Redraw table once pageLength changes,
	 * Don't try to redraw if data table hasn't initialized yet.
	 */
	private pageLengthChanged(length, oldLength) {
		const hasDrawnTable = oldLength !== undefined;
		if (hasDrawnTable && length && oldLength !== length) {
			// NOTE(Jake): 2018-10-02 - WEBUI-1025
			// Update DataTable to use new page length.
			// This was added so that AcurityTable within a dialog
			// would use the new page length.
			const dataTable = this.getDataTable();
			if (dataTable) {
				const oldPageLength = dataTable.page.len();
				if (oldPageLength !== this.pageLength) {
					Logger.log(
						'Page length:',
						oldPageLength,
						'->',
						length,
						'... Updating/Redrawing table',
					);
					dataTable.page.len(this.pageLength);
					dataTable.draw();

					// Update scrollbars
					if (this.perfectScroll) {
						this.perfectScroll.update();
					}
				}
			}
		}
	}

	// Getters
	public onTableInitialised() {
		return this.tableReady;
	}

	/**
	 * onAjaxLoaded is a Promise that will only trigger ONCE.
	 */
	public onAjaxLoaded(): LockUntilReady {
		return this.ajaxLoaded;
	}

	public getAjaxTableDataSet(): AcurityTableRecord[] {
		const dt = this.getDataTable();
		if (!dt) {
			return [];
		}
		const recordList = dt.data().toArray() as AcurityTableRecord[];
		if (recordList === null || recordList === undefined) {
			return [];
		}
		return recordList;
	}

	/**
	 * currentRowGetter retrives the table row element currently selected.
	 */
	public currentRowGetter = (): JQuery<HTMLElement> | null => {
		if (
			this.jqueryTableReference &&
			this.currentSelection &&
			this.currentSelection.row &&
			this.currentSelection.row.length > 0 &&
			this.currentSelection.row[0].id
		) {
			const escapedRowId = $.escapeSelector(
				this.currentSelection.row[0].id,
			);
			const $tableRowEl = this.jqueryTableReference.find(
				'tr#' + escapedRowId,
			);
			if ($tableRowEl.length > 0) {
				return $tableRowEl;
			}
		}
		return null;
	};

	public isDataTable(): boolean {
		return ($.fn.DataTable as any).isDataTable(this.jqueryTableReference);
	}

	private getTableOverlayReference() {
		return $('[table-id="' + this.tableId + '"] .a-table-overlay');
	}

	private getTableReference() {
		return $(this.jqueryTableReference);
	}

	/**
	 * There are areas in slideout.ts and elsewhere in the code that simply use
	 * jQuery to get that table ID. This should not be how we get a reference of the
	 * table object.
	 *
	 * As a stop-gap solution, this function will exist so that we can retrieve information
	 * about the table.
	 *
	 * This should not be used for new functionality and should be avoided.
	 *
	 * @deprecated since version 19.0.0.248 DEVEL
	 */
	public UNSAFE_DEPRECATED_getTableReference() {
		return $(this.jqueryTableReference);
	}

	/**
	 * NOTE(Jake): 2018-10-03 - WEBUI-1051
	 * Get data table instance but only if it's initialized.
	 *
	 * We should use this method to ensure we don't cause "can't reinitialize twice"
	 * crashes. This method was introduced to fix a bug where "pageLengthChanged" fired
	 * before the datatable was properly initialized, causing E2E tests to break.
	 */
	public getDataTable(): DataTables.Api | undefined {
		if (!this.isDataTable()) {
			// Logger.warning('Trying to use data table before it\'s initialized');
			return undefined;
		}
		const $table = this.getTableReference();
		const dataTableInstance = $table.DataTable();
		return dataTableInstance;
	}

	get readOnlyRecords(): number[] {
		return this._readOnlyRecords;
	}

	// Setters
	public setAjaxData(ajaxData: (data: DataTablesRequest) => void) {
		if (this.ajaxData) {
			throw new Error('Cannot call setAjaxData() more than once.');
		}
		this.ajaxData = ajaxData;
	}

	public getAjaxURL(): string {
		return this.ajaxURL;
	}

	public setAjaxURL(url: string) {
		if (this.ajaxURL !== url) {
			this.ajaxURL = url;
		}
	}

	// Table Initialisation
	/**
	 * init normal tables, support pagination as well, but the pagination will be in memory
	 * @param dataSet the data array you want to display.
	 * @param fieldsInfo fields info, including name, label, type, code list.
	 * @param iconMap optional parameter, the icon map for this table.
	 */
	public initNormalTable(
		dataSet: object[],
		fieldsInfo: ColumnDefinitionMap,
		iconMap?: any,
	) {
		if (this.defaultColumnInfo) {
			Logger.warning(
				'initNormalTable cannot be initialized twice.',
				this.defaultColumnInfo,
			);
			return;
		}
		if (iconMap) {
			this.iconMap = iconMap;
		}

		this.tableDataSet = dataSet;
		this.defaultColumnInfo = fieldsInfo;
		this.initNormalTableData();
		this.initTableColumnInfoAndTableColumnSettings();

		// NOTE: Jake: 2018-10-11 - WEBUI-1110
		// AcurityTable on the ATO Interfaces page needs this or the
		// table will never initialize and just not show up.
		// Specifically, if you open a record with a "Data Compare" tab and click
		// it.
		// We need the `(!this.isDataTable())` check as Client Summary currently gets
		// a double initialization error without it.
		// We seemingly don't need to use this "waitUntilVisible" promise however.
		// ElementUtils.waitUntilVisible(this.element).then(() => {
		if (!this.isDataTable()) {
			this.initNormalTableOptions();
		}
		// });
	}

	private initNormalTableData(descriptionMap?: {
		[fieldName: string]: DescriptionDefinition;
	}) {
		this.rowId = 1;
		this.updateRowDataWithMoreInformation(
			this.tableDataSet,
			descriptionMap,
		);
	}

	// Custom implementation of the datetime-moment plugin (https://datatables.net/blog/2014-12-18).
	// The contents of this function comes from: node_modules/datatables.net-plugins/sorting/datetime-moment.js
	// We make a slight modification to this plugin to handle escaped '/' symbols that appear in our dates.
	// This is used for sorting dates on non-AJAX tables (primarily, audit tables).
	private initAutoDateSorting() {
		const types = $.fn.dataTable.ext.type;
		const format = moment.localeData().longDateFormat('L');

		// Add type detection
		types.detect.unshift((d) => {
			if (d) {
				// Strip HTML tags and newline characters if possible
				if (d.replace) {
					d = d.replace(/(<.*?>)|(\r?\n|\r)/g, '');
					// Modification for Acurity: replace escaped slashes
					d = d.replace(/&#x2F;/g, '/');
				}

				// Strip out surrounding white space
				d = $.trim(d);
			}

			// Null and empty values are acceptable
			if (d === '' || d === null) {
				return 'moment-' + format;
			}

			return moment(d, format, null, true).isValid()
				? 'moment-' + format
				: null;
		});

		types.order['moment-' + format + '-pre'] = (d) => {
			if (d) {
				// Strip HTML tags and newline characters if possible
				if (d.replace) {
					d = d.replace(/(<.*?>)|(\r?\n|\r)/g, '');
					// Modification for Acurity: replace escaped slashes
					d = d.replace(/&#x2F;/g, '/');
				}

				// Strip out surrounding white space
				d = $.trim(d);
			}

			return !moment(d, format, null, true).isValid()
				? Infinity
				: parseInt(moment(d, format, null, true).format('x'), 10);
		};
	}

	// see https://datatables.net/reference/ for info on underlying jquery table
	private initNormalTableOptions() {
		if (!this.jqueryTableReference) {
			Logger.warning(
				'Cannot use <table> element. Did you wait for attached() to execute?',
				this.jqueryTableReference,
			);
			return;
		}
		if (this.jqueryTableReference.length === 0) {
			Logger.warning('Cannot use <table> element. No element found.');
			return;
		}

		// need to register additional date format so normal non-ajax table can detect and order by date, not just by alphabetical string
		this.initAutoDateSorting();

		// normal table specific options
		const options: DataTables.Settings = this.getBaseTableOptions();
		this.setNormalTableOptions(options);

		// Only apply pageLength if we're not filling up the table
		{
			this.calculatePageLengthForFillTable();
			if (!this.fillTable) {
				// If not dynamically filling based on table height, use page length
				options.pageLength = this.pageLength || 5;
			}
		}

		Logger.log(
			`initNormalTableOptions: Setting up DataTable: #${this.jqueryTableReference.attr(
				'id',
			)}`,
		);
		this.jqueryTableReference.DataTable(options);
		// DataTable initialisation may fail without throwing an error. We must check for it manually.
		if (!this.isDataTable()) {
			throw new Error('Set up of Normal DataTable failed');
		}

		this.initContextMenuEvents();
	}

	protected setNormalTableOptions(options: DataTables.Settings) {
		options.data = this.tableDataSet;
		options.processing = false;
		options.autoWidth = this.autoWidth;
	}

	private destroyDataTable(): void {
		const dataTable = this.getDataTable();
		if (dataTable) {
			Logger.log(
				`Destroying DataTable: #${this.getTableReference().attr('id')}`,
			);
			dataTable.destroy();
			// NOTE: Jake: 2019-02-06 - WEBUI-1219
			// Without this, we get errors like:
			// "jquery.dataTables.js:4089 Uncaught TypeError: Cannot read property 'style' of undefined"
			// https://stackoverflow.com/questions/39376658/datatables-cannot-read-property-style-of-undefined
			this.jqueryTableReference.empty();
		}
	}

	private recreateWithoutTriggeringAjax(): void {
		const dataTable = this.getDataTable();
		if (!dataTable) {
			return;
		}
		if (!this.ajaxURL) {
			throw new Error(
				'Can only call recreateWithoutTriggeringAjax() for AJAX tables.',
			);
		}
		this.ignoreAjaxOnceNextCall = true;
		this.initTableColumnInfoAndTableColumnSettings();
		this.initAjaxTablePhaseTwo();
	}

	/**
	 * tableColumnsChanged will only trigger if the array itself is rebuilt / changed.
	 */
	private tableColumnsChanged(
		newValue: TableColumnDefinition[],
		oldValue: TableColumnDefinition[],
	): void {
		const dataTable = this.getDataTable();
		if (!dataTable) {
			return;
		}
		if (
			dataTable.context &&
			dataTable.context.length > 0 &&
			dataTable.context[0].aoColumns
		) {
			for (const dtColumnHeader of dataTable.context[0].aoColumns) {
				const index = dtColumnHeader.idx;
				const fieldName = dtColumnHeader.data;

				// NOTE: Jake: 2019-10-24 - WEBUI-2626
				// Added logic so "tableColumns" can be modified so that
				// "hiddenFrom" changes take affect on the DataTable.
				for (const columnInfo of this.tableColumns) {
					if (columnInfo.name === fieldName) {
						const visible = dataTable.column(index).visible();
						const newVisible = !AcurityTable.isHiddenIn(
							columnInfo,
							HiddenTarget.TABLE,
						);
						if (visible !== newVisible) {
							dataTable.column(index).visible(newVisible, false);
						}
						break;
					}
				}
			}
		}
		// NOTE: Jake: 2019-10-25 - WEBUI-2626
		// We specificially do not call:
		// dataTable.draw(false);
		//
		// The reason is that for if we did, it would re-trigger
		// an AJAX request. However, because for our use-case
		// "tableColumnsChanged" is being triggered within the "onFilter" hook
		// the table is going to be re-drawn anyway.
		//
		// After lots of googling, it seems like causing a re-render without
		// firing the AJAX call is not possible.
	}

	private onDataTableInfoCallback = (
		settings,
		start,
		end,
		max,
		total,
		pre,
	) => {
		// On empty tables, we can get start = 1 and end = 0.
		if (start > end) {
			start = end;
		}

		if (this.recordsTotal == null) {
			if (total === 0) {
				return this.i18n.tr(
					'datatables.info-async-still-counting-no-records',
					{
						start,
						end,
					},
				);
			} else {
				return this.i18n.tr('datatables.info-async-still-counting', {
					start,
					end,
				});
			}
		} else if (this.recordsTotal === AcurityTable.COUNT_UNAVAILABLE) {
			return this.i18n.tr('datatables.info-async-count-unavailable', {
				start,
				end,
			});
		} else {
			let totalText;
			if (this.recordsTotalExceedsLimit) {
				totalText = this.recordsTotal + '+';
			} else {
				totalText = this.recordsTotal;
			}
			if (total === 0) {
				return this.i18n.tr('datatables.info-async-no-records', {
					start,
					end,
					total: this.recordsTotal,
				});
			} else {
				return this.i18n.tr('datatables.info-async', {
					start,
					end,
					total: totalText,
				});
			}
		}
	};

	private transformAjaxResponse = (json): void => {
		json.recordsFiltered = 0;
		if (json.nextPageAvailable === true) {
			// While a hack; this seems to make the rest of DataTables functionality behave how we want it to quite nicely.
			// If set this to (fakeRecordCount + 1), we have issues with jumping to a specific page, and with actions like
			// rapidly navigating forward pages (by holding the PAGE DOWN key).
			json.recordsFiltered = Number.MAX_SAFE_INTEGER;
		} else if (json.data.length > 0) {
			const dataTable = this.getDataTable();
			if (dataTable) {
				// NOTE: Jae: 2020-01-29 - WEBUI-2945
				// Had to wrap this in "getDataTable()" to stop the error
				// "Cannot read property 'page' of undefined"
				// I was able to reproduce by quick clicking to a different form
				// page while the table is loading.
				const currentPageInfo = dataTable.page.info();
				const fakeRecordCount =
					currentPageInfo.page * currentPageInfo.length +
					json.data.length;
				json.recordsFiltered = fakeRecordCount;
			}
		}
		json.recordsTotal = json.recordsFiltered;
	};

	/**
	 * initAjaxTable will initialize the table with AJAX-style pagination.
	 */
	public initAjaxTable(ajaxURL?: string, iconMap?: any) {
		this.useAdvancedPagination = true;
		if (this.isDataTable() && this.ajaxURL === ajaxURL) {
			// Ignore re-init if table is initialized already and the ajax URL hasn't changed.
			return;
		}
		if (
			!this.jqueryTableReference ||
			this.jqueryTableReference.length === 0
		) {
			Logger.warning(
				`acurity-table: Unable to retrieve table (#${this.tableId})`,
			);
			return;
		}
		this.ajaxURL = ajaxURL;
		if (iconMap) {
			this.iconMap = iconMap;
		}
		this.initTableColumnInfoAndTableColumnSettings();

		// Setup
		Logger.log(
			`initAjaxTable: Setting up DataTable: #${this.jqueryTableReference.attr(
				'id',
			)}`,
		);
		this.destroyDataTable();
		this.jqueryTableReference.DataTable({
			...this.getBaseTableOptions(),
			serverSide: false, // NOTE(Jake): 2018-10-10: Explictly "false" for first render of table. We set this to "true" after calc'ing table height.
			pageLength: 1337, // NOTE(Jake): 2018-10-10: Here for debug purposes, otherwise it defaults to 10 which can make things unclear in debugging.
		});
		// DataTable initialisation may fail without throwing an error. We must check for it manually.
		if (!this.isDataTable()) {
			throw new Error('Set up of AJAX DataTable failed');
		}

		this.forceInitScrollbarStyling();
		// NOTE(Jake): 2018-10-10
		// Hide ugly table flickering while we calculate the element height.
		// Visibility retains element computations.
		this.jqueryTableReference[0].style.visibility = 'hidden';

		window.requestAnimationFrame(() => {
			this.initAjaxTablePhaseTwo();
		});
	}

	/**
	 * initAjaxTablePhaseTwo initializes the table after an first render/initialization with dummy or no data.
	 * The reason we take this approach is so we can compute the height of the DataTable based on the DOM in
	 * the method "calculatePageLengthForFillTable".
	 *
	 * This method is also called by "recreateWithoutTriggeringAjax" so that the table can be reinitialized with
	 * columns received from the server.
	 */
	private initAjaxTablePhaseTwo = (): void => {
		if (!this.getDataTable()) {
			return;
		}
		let accessToken = getItem(LoginConstants.ACCESS_TOKEN);
		if (OktaLogin.isLoggedIn()) {
			// Tokens should not normally be read from storage as we cannot verify the validity of the token.
			// This simply checks for the presence of an access token. OktaLogin.getAccessToken() will be used
			// to send the correct token to the table API.
			const tokens: Tokens = JSON.parse(getItem(OKTA_TOKEN_STORAGE));
			accessToken =
				tokens && tokens.accessToken && tokens.accessToken.accessToken
					? tokens.accessToken.accessToken
					: undefined;
		}
		if (!accessToken) {
			throw new Error('Cannot use accessToken on DataTable.');
		}

		// NOTE(Jake): 2018-10-10 - WEBUI-1060
		// Waiting 1 frame for <div> sizes to be affected by mock
		// DataTable initialization. If you do not wrap this in window.requestAnimationFrame
		// the DataTable will calculate the incorrect height.
		this.calculatePageLengthForFillTable();

		//
		const newOptions: DataTables.Settings = {
			...this.getBaseTableOptions(),
			serverSide: true,
			processing: this.processing,
			pagingType: 'full',
			pageLength: this.pageLength ? this.pageLength : 5,
			infoCallback: this.onDataTableInfoCallback,
			ajax: (
				data: object,
				callback: (data: any) => void,
				settings: DataTables.SettingsLegacy,
			): void => {
				return this.onDataTableAjaxCallback(data, callback, settings);
			},
		};

		// Destroy
		this.destroyDataTable();

		// NOTE(Jake): 2018-10-10 - WEBUI-1060
		// Wait 1 more frame after destroying the datatable, just so
		// the DOM changes occur first. If you don't wrap this in window.requestAnimationFrame
		// or similar, two network requests will fire with the same parameters.
		window.requestAnimationFrame(() => {
			// WEBUI-1487, 1293 The isDetached check ensures that we don't proceed with the initilization of discarded table instance
			// when a another instance of AcurityTable has been created in its place.
			if (this.getDataTable() || this.isDetached) {
				// WEBUI-1293
				// DataTable is expected to be null here as it was destroyed in last frame. We can still find
				// its reference implies that it has been reinitialized in this frame because of navigation
				// changes or something else. So this callback is invalid now.
				return;
			}
			// NOTE(Jake): 2018-10-10 - WEBUI-1060
			// Wait 1 more frame after destroying the datatable, just so
			// the DOM changes occur first. If you don't wrap this in window.requestAnimationFrame
			// or similar, two network requests will fire with the same parameters.
			Logger.log(
				`calculatePageLength: Setting up DataTable: #${this.jqueryTableReference.attr(
					'id',
				)}`,
			);
			this.jqueryTableReference.DataTable(newOptions);
			// DataTable initialisation may fail without throwing an error. We must check for it manually.
			if (!this.isDataTable()) {
				throw new Error(
					'Set up of AJAX DataTable failed (calculatePageLength)',
				);
			}
			this.jqueryTableReference[0].style.visibility = '';
			this.forceInitScrollbarStyling();
		});
	};

	private readonly onDataTableAjaxCallback = (
		data: object,
		callback: (data: any) => void,
		settings: DataTables.SettingsLegacy,
	): void => {
		const dataTable = this.getDataTable();
		if (!dataTable) {
			// NOTE: Jae: 2020-01-29
			// Avoid fetching results if the table element is detached / removed.
			return;
		}
		let promise: Promise<DataTablesResponse>;
		if (this.ignoreAjaxOnceNextCall && !this.ajaxReturnedFilter) {
			this.ignoreAjaxOnceNextCall = false;
			this.ajaxReturnedFilter = false;
			if (!this.previousDataTablesResponse) {
				throw new Error(
					'Missing previous response. "recreateWithoutTriggeringAjax" may have been called before an AJAX response was returned from the server.',
				);
			}

			// NOTE(Jae): 2020-04-29 - WEBUI-3202
			// We need to call this so state is all ok.
			// ie. CSV export wont work without this here.
			this.onDataTableAjaxData(data as DataTablesRequest);

			const previousDataTablesResponse = JSON.parse(
				this.previousDataTablesResponse,
			);
			this.previousDataTablesResponse = undefined;
			promise = Promise.resolve(previousDataTablesResponse);
		} else {
			const processedRequestVars: { [fieldName: string]: any } =
				this.onDataTableAjaxData(data as DataTablesRequest);
			promise = FetchUtils.getJSON<DataTablesResponse>(
				this.ajaxURL,
				processedRequestVars,
				{
					extractParamsFromURL: true,
				},
			)
				.then((dataTablesResponse: DataTablesResponse) => {
					this.previousDataTablesResponse =
						JSON.stringify(dataTablesResponse);
					return dataTablesResponse;
				})
				.then((dataTablesResponse: DataTablesResponse) =>
					this.maybeUpdateTableColumnsFromData(dataTablesResponse),
				)
				.then((dataTablesResponse: DataTablesResponse) => {
					this.publishEvent(AcurityTable.Events.AjaxDataReady, {
						table: this,
					});
					return dataTablesResponse;
				});
		}
		promise
			.then((dataTablesResponse: DataTablesResponse) =>
				this.onDataTableAjaxDataFilter(dataTablesResponse),
			)
			.then((returnData: DataTablesResponse) => callback(returnData))
			.catch((error) => {
				const message = FetchUtils.normalizeError(error);
				if (error.status && error.status === httpStatus.BAD_REQUEST) {
					Notification.error(
						this.i18n.tr('errors.unable-to-retrieve-records', {
							message,
						}),
					);
					return;
				}
				Notification.error(message);
				this.tableLoadingMessage = message;
				Logger.error('onDataTableAjaxError: ' + message);
				throw new Error(message);
			});
	};

	private maybeUpdateTableColumnsFromData = (
		dataTablesResponse: DataTablesResponse,
	): DataTablesResponse => {
		// NOTE(Albert): 2020-06-18 - WEBUI-3499
		// We only want to recreate table if DataTablesResponse contains a tableConfig on first page load.
		// If we do this every time table is redrawn (eg changing page, reverse order, sorting columns),
		// it breaks this front end functionality because these states do not persist. Once response
		// tableConfig has already been loaded, it does not need to be repeated on every table draw
		if (
			dataTablesResponse.tableConfig &&
			dataTablesResponse.tableConfig.tableColumns &&
			dataTablesResponse.tableConfig.tableColumns.length > 0 &&
			this.recreateTableFromResponseConfig
		) {
			this.recreateTableFromResponseConfig = false;
			const tableConfig = dataTablesResponse.tableConfig;
			if (!this.tableColumns || this.tableColumns.length === 0) {
				// Only use backend defaults if we have no table columns set
				this.tableColumns = this.convertTableColumnsFromConfig(
					tableConfig.tableColumns,
					true,
				);
			}

			// Append any front end only columns to columns provided by back end
			if (
				this.frontEndTableColumns &&
				this.frontEndTableColumns.length > 0
			) {
				this.tableColumns.push(...this.frontEndTableColumns);
			}

			// If fieldConfig has dataType values available, use these to set the dataType on the table columns
			if (this.fieldConfigDataTypeMap) {
				this.tableColumns.forEach((column) => {
					const dataType = this.fieldConfigDataTypeMap.get(
						column.name,
					);
					if (dataType) {
						column.dataType = dataType;
					}
				});
			}

			// Check required fields for errors
			const errors: string[] = [];

			// NOTE(Jae): 2020-05-04 - WEBUI-3196
			// To support customisable table columns, we need to make sure we apply
			// the backend "rowIdKey" (and other values) if its not set here. Otherwise users cannot open
			// records after they customise a table.
			if (!this.rowIdKey) {
				if (!tableConfig.rowIdKey) {
					errors.push('rowIdKey');
				}
				this.rowIdKey = tableConfig.rowIdKey;
			}
			if (!this.multiColumnOrder) {
				if (!tableConfig.multiColumnOrder) {
					errors.push('multiColumnOrder');
				}
				this.multiColumnOrder = tableConfig.multiColumnOrder;
			}
			if (errors.length > 0) {
				const errorMessage = new Error(
					'Missing table info from backend:\n' + errors.join('\n'),
				);
				Notification.error(errorMessage);
				throw errorMessage;
			}
			// Non-required fields
			if (!this.deletedHighlightField) {
				this.deletedHighlightField = tableConfig.deletedHighlightField;
			}
			if (!this.canceledHighlightField) {
				this.canceledHighlightField =
					tableConfig.canceledHighlightField;
			}
			this.recreateWithoutTriggeringAjax();
		}
		return dataTablesResponse;
	};

	private onDataTableDraw = (): void => {
		const dataTable = this.getDataTable();
		if (!dataTable) {
			return;
		}
		this.setupSorting();

		// Add filter icon to table header
		{
			// NOTE: Jake: 2019-01-16 - WEBUI-1239
			// This has seemingly been broken for a while?
			// All this logic works however I'm going to keep it incase we want to bring it back.
			/* this.jqueryTableReference.find('thead th.i-sortable i.filter').removeClass('fa-filter');
			this.tableColumnInfo.forEach(column => {
				if (column.filter) {
					// NOTE: Jake: 2019-01-15 - WEBUI-1239
					// This logic was in-place previously though not sure if it'll have
					// the correct behaviour if "value" is string[]. Might need to be fixed later.
					const columnFilterValue = column.filter.value ? String(column.filter.value).trim() : '';

					if (columnFilterValue !== '' &&
						columnFilterValue !== FilterType.NO_FILTER) {
						// add filter icon next to column header to show which columns have filtering
						const filteredColumn = this.jqueryTableReference.find('thead th.i-sortable#th-' + column.name);
						if (filteredColumn.length === 0) {
							Logger.warning('Cannot apply filter icon to table header for column:', column.name);
							return;
						}
						const icon = filteredColumn.find('i.filter');
						if (icon.length === 0) {
							Logger.warning('Cannot apply filter icon to table header for column, missing "i.filter" element:', column.name);
							return;
						}
						icon.addClass('fa-filter');
					}
				}
			});*/
		}

		// add back column padding
		if (this.sortable) {
			const $tWrapper = $('#' + this.tableId + '_wrapper');
			$tWrapper.find('.i-sortable').addClass('expand');
		}

		const newRecordsTotal = dataTable.page.info().recordsTotal;
		if (this.maxResultSize !== newRecordsTotal) {
			this.maxResultSize = newRecordsTotal;
		}

		// NOTE: Jake: 2018-11-09 - WEBUI-1161
		// If the table was re-drawn or initialized, make sure
		// we re-query the currentSelection.row.
		if (
			this.currentSelection &&
			this.currentSelection.row &&
			((this.currentSelection.row !== -1 &&
				this.currentSelection.row.length > 0) ||
				this.selectedObjId)
		) {
			const id = this.selectedObjId; // this.currentSelection.row[0].getAttribute('id');
			if (id) {
				const escapedRowId = $.escapeSelector(id);
				const specificRow = dataTable.$('tr#' + escapedRowId).first();
				if (specificRow.length === 0) {
					this.currentSelection.row = -1;
					this.currentSelection.page = -1;
				} else {
					this.currentSelection.row = specificRow;
					this.currentSelection.page = dataTable.page.info().page;
					this.currentSelection.row.addClass('selected');
				}
			}
		}

		if (this.perfectScroll) {
			this.perfectScroll.update();
		}

		if (this.onDraw) {
			this.onDraw(this, this.jqueryTableReference);
		}

		this.addPaginateTooltips();

		// select record if table only has 1 record and is not already selected
		if (
			this.selectIfOnlyOne &&
			this.recordsTotal === 1 &&
			this.currentRowGetter() === null
		) {
			this.selectSpecificRecord(RecordIdentifier.FIRST).then(($row) => {
				this.openSelected();
			});
		}
	};

	private onClickRowIfClickListenerSet = (e: JQuery.ClickEvent) => {
		if (!this.clickListener) {
			Logger.error(
				'this.clickListener is not set. This should not happen.',
			);
			return;
		}
		const target = e.target as HTMLElement;
		// Ignore click on table row if we clicked a <button> inside
		// the table row.
		if (target instanceof HTMLButtonElement) {
			// Ignore that we clicked the row if we actually clicked a <button>
			// This helps ignoring special buttons rendered within the table.
			return;
		}
		if (target.querySelector('button')) {
			// Ignore if <button> exists within row or column, ie. <tr> or <td>
			return;
		}
		let parentEl = target.parentElement;
		while (
			parentEl &&
			parentEl.tagName !== 'TR' &&
			parentEl.tagName !== 'BODY'
		) {
			if (parentEl.tagName === 'BUTTON') {
				// Ignore if clicked <span> inside <button>
				return;
			}
			parentEl = parentEl.parentElement;
		}
		const tableRef = this.getDataTable();
		if (!tableRef) {
			return;
		}

		// record the current selected item
		const el = e.currentTarget;
		this.setSelected($(el), tableRef.page.info().page);
		this.openSelected();
	};

	/**
	 * Iterates over a list of {@link AcurityTableRecord}s and, where necessary, sets the ID attribute and/or the
	 * Deleted/Cancelled highlight class for each record. This should not be called if the list of records is empty.
	 */
	private setRowIdAndStyling(tableDataSet: AcurityTableRecord[]) {
		for (const index in tableDataSet) {
			if (isNaN(Number(index)) || !tableDataSet[index]) {
				continue;
			}
			const record = tableDataSet[index];

			// For tables that load data after page load, eg Beneficiaries table loads
			// data when expanding member card on Client Summary, need to set DT_RowId
			// on record only after data is loaded if DT_RowId does not already exist
			if (this.loadTableOnDataReady && !record.DT_RowId) {
				this.rowId = 1;
				record.DT_RowId = this.rowIdKey
					? record[this.rowIdKey].value
					: this.rowId++;
				if (DEVELOPMENT && record.DT_RowId === undefined) {
					const message =
						'Invalid rowId computed for dataset. See console.';
					Logger.error(message, tableDataSet);
					throw new Error(message);
				}

				// DataTables library does not automatically set id attribute on table
				// row based on DT_RowId value in this case (I suspect because data is
				// not loaded when table is initialised) so set id attribute to row
				this.tableEl
					.querySelectorAll('tbody tr')
					[parseInt(index, 10)].setAttribute(
						'id',
						record.DT_RowId.toString(),
					);
			}

			const escapedRowId = $.escapeSelector(
				record.DT_RowId !== undefined ? record.DT_RowId.toString() : '',
			);
			{
				// for non-ajax tables, tableDataSet is bound to a list of fieldModelMaps
				// rather than valueMaps so field is an object, not a string
				const deletedField = record[this.deletedHighlightField];
				let deletedFieldValue: string =
					deletedField && typeof deletedField === 'object'
						? deletedField.value
						: deletedField;
				if (!deletedFieldValue) {
					deletedFieldValue = '';
				}

				const canceledField = record[this.canceledHighlightField];
				let canceledFieldValue: string =
					canceledField && typeof canceledField === 'object'
						? canceledField.value
						: canceledField;
				if (!canceledFieldValue) {
					canceledFieldValue = '';
				}

				if (
					deletedFieldValue.substr(0, 1) === 'Y' ||
					canceledFieldValue.substr(0, 1) === 'C'
				) {
					const rowEl = this.tableEl.querySelector(
						'tr#' + escapedRowId,
					);
					if (rowEl) {
						rowEl.classList.add('danger');
					}
				}
			}
		}
	}

	/**
	 * Sets the click and focus listeners for each table row and sets a tooltip for table
	 * cells that have a description. This should not be called if the table is empty.
	 * setRowListenerAndDescription is overriden by data-compare-tab.ts currently.
	 */
	protected setRowListenerAndDescription($tableRows: JQuery) {
		if ($tableRows[0] && (!$tableRows[0].id || $tableRows[0].id === '')) {
			Logger.error(
				'Invalid table rows, missing "id" attr which is needed for selecting.',
			);
			return;
		}
		// NOTE: Jake: 2018-10-15 - WEBUI-1108
		// Click and focus events are only applied on tables that have setup a
		// clickListener. This is so that you cannot click on records in the Client Summary
		// area and trigger errors.
		$tableRows.off('click');
		$tableRows.removeClass('clickable');

		if (this.clickListener) {
			// Allow selecting each row of the table with mouse
			$tableRows.addClass('clickable');
			$tableRows.on('click', this.onClickRowIfClickListenerSet);
		}

		$tableRows.each((_, el) => {
			// Allow focusing / tabbing on the row (adds a blue highlight when tabbing as of 2018-10-12)
			if (this.rowIdKey) {
				// If table does not have a rowIdKey, there should be no need to allow a row to be focused
				// This check prevents an error when trying to read this.selectedObj in this.setSelected()
				this.setFocusListener($(el));
			}

			const visibleColumns = this.tableColumnInfo
				// filtered out the hidden columns
				.filter(
					(column) =>
						!(
							column.hiddenFrom &&
							Array.isArray(column.hiddenFrom) &&
							column.hiddenFrom.includes(HiddenTarget.TABLE)
						),
				);

			// add title to sortable columns that have descriptions (exclude context menu column)
			$(el)
				.find('td:not(.a-table-context-icon)')
				.each((dataTableColIndex, tableCell) => {
					visibleColumns.forEach((column, colInfoIndex) => {
						const elTableCell = $(tableCell);
						const enableToolTipFlag = column.enableToolTip === true;
						const cellValue = elTableCell.text();
						const description = enableToolTipFlag
							? cellValue
							: cellValue.substring(
									cellValue.indexOf('(') + 1,
									cellValue.lastIndexOf(')'),
							  );
						if (
							(column.isCodeListLoadedWithData ||
								column.codeList ||
								enableToolTipFlag) &&
							dataTableColIndex === colInfoIndex &&
							description.trim() !== ''
						) {
							if (column.wideColumn) {
								elTableCell.addClass('description__extra-wide');
							} else {
								elTableCell.addClass('description');
							}
							elTableCell.attr(
								'data-original-title',
								description,
							);
							elTableCell.tooltip({
								delay: { show: 500, hide: 100 },
								placement: 'right',
								html: false,
							});
						}
					});
				});
		});
	}

	public addPaginateTooltips(): void {
		const paginateElements = new Map<string, readonly Key[]>([
			['first', ShortcutKey.FirstPage],
			['previous', ShortcutKey.PrevPage],
			['next', ShortcutKey.NextPage],
			['last', ShortcutKey.LastPage],
		]);

		paginateElements.forEach((shortcutKeys, elementSelector) => {
			const shortcutElement: Element = this.element.querySelector(
				'.dataTables_paginate .' + elementSelector + ' a',
			);
			if (shortcutElement) {
				KeyboardShortcut.addTooltip(
					shortcutElement,
					KeyboardShortcut.keysToString(shortcutKeys),
					this,
				);
			}
		});
	}

	private onDataTableInit(): void {
		this.tableReady.triggerOnce();
	}

	private onDataTableProcessing(e: JQuery.Event, settings, processing): void {
		this.toggleSpinner(processing);
	}

	private onDataTableDrawCallback(settings: DataTables.SettingsLegacy): void {
		this.initContextMenuEvents();

		const dt = this.getDataTable();
		if (dt) {
			if (this.useAdvancedPagination) {
				this.removePaginationLastButton();
			}

			const tableDataSet = dt.data() as any as AcurityTableRecord[];
			if (tableDataSet) {
				if (tableDataSet.length === 0) {
					Logger.log('Not setting row attributes on empty table.');
				} else {
					this.setRowIdAndStyling(tableDataSet);
					const $tableRows =
						this.getTableReference().find('tbody tr');
					this.setRowListenerAndDescription($tableRows);
				}
			}
		}
	}

	private onDataTableXHR = (e, settings, json, xhr): void => {
		// Trigger promises registered by other classes like filter.ts
		this.onAjaxLoaded().triggerOnce(json);
	};

	private onDataTableError = (
		e,
		dataTableParams,
		techNote: number,
		message: string,
	) => {
		throw new Error(message);
	};

	private onDataTableAjaxData = (
		data: DataTablesRequest,
	): DataTablesRequest => {
		this.filterColumnsAndOrder(data);

		// NOTE: Jake: 2019-05-15
		// Ensure the "data.filters" structure is an array
		if (data.filters === undefined) {
			data.filters = [];
		}

		// NOTE: Jake: 2019-05-15 - WEBUI-1971
		// "classic-form" applies url parameters to filters, ie. "fund", "member".
		// This logic used to be applied after "createNormalisedRequestFilters", however, this
		// meant that if a user tried searching by "fund" *after* selecting a member entity, it
		// would just default to their current "fund".
		//
		// See the JIRA tagged above for more information if this is unclear.
		if (this.ajaxData !== undefined) {
			this.ajaxData(data);
		}

		{
			const validTableColumnInfoRequestFilters =
				this.createNormalisedRequestFilters(this.tableColumnInfo);

			// Apply filters to the request
			data.filters.push(...validTableColumnInfoRequestFilters);

			// add filter icon next to column headers to show which columns have filtering
			validTableColumnInfoRequestFilters.forEach((filter) => {
				const filteredColumn = this.jqueryTableReference.find(
					'thead th.i-sortable#th-' + filter.data,
				);
				const icon = filteredColumn.find('i.filter');
				icon.addClass('fa-filter');
			});
		}

		if (data.columns) {
			this.addAdditionalTableColumns(data.columns);
		}
		this.updateColumnSortOrder(data);

		// Update `pageLength`
		const pageLength = this.pageLength;
		data.length = pageLength;
		const dataTable = this.getDataTable();
		if (dataTable) {
			const oldPageLength = dataTable.page.len();
			if (oldPageLength !== pageLength) {
				dataTable.page.len(pageLength);
			}
		}

		// Apply reverse order flag
		data.reverseOrder = dataTable.settings()[0].reverseOrder;

		// NOTE: York: 2012-02-20 - WEBUI-3067
		// It is intention that `onFilter` can modify data.filters.
		if (this.onFilter) {
			this.onFilter(data.filters);
		}

		// Count only
		data.mode = 'count_only';
		this.doAsyncCount(data).catch((e: Error) => {
			if (e.message && e.message.includes('timed out')) {
				Notification.warning(
					this.i18n.tr('datatables.counting-total-timed-out'),
				);
			} else {
				Notification.error(e);
			}

			this.recordsTotal = AcurityTable.COUNT_UNAVAILABLE;
			this.refreshDataTableInfo();
		});

		data.mode = 'data_only';
		return data;
	};

	private async doAsyncCount(countQueryParams: {
		[param: string]: any;
	}): Promise<void> {
		const countModel: DataTablesResponse = await FetchUtils.getJSON(
			this.ajaxURL,
			countQueryParams,
			{
				extractParamsFromURL: true,
			},
		);

		if (!countModel) {
			throw new Error('Missing response from table API "count_only".');
		}
		this.recordsTotal = countModel.recordsTotal;
		this.recordsTotalExceedsLimit = countModel.recordsTotalExceedsLimit;
		this.refreshDataTableInfo();
	}

	private filterColumnsAndOrder(data: DataTablesRequest) {
		if (data.columns) {
			// NOTE: Jake: 2018-11-02 - WEBUI-1089
			// If no "data" or "name" associated with column, assume it's a special
			// case and that we don't want that column to be requested from the server.
			const removedColumnIndices: number[] = [];
			data.columns = data.columns.filter((column, index, arr) => {
				const fieldName = column.data; // ie. THi_Identity, THz_Payroll, etc
				if (
					!fieldName ||
					(fieldName && this.isLocalColumn(fieldName))
				) {
					removedColumnIndices.push(index);
					return false;
				}
				return true;
			});

			if (
				data.order &&
				data.order.length > 0 &&
				removedColumnIndices.length > 0
			) {
				removedColumnIndices.forEach((colIndex) => {
					data.order = data.order.filter((order) => {
						return order.column !== colIndex;
					});

					data.order.forEach((order) => {
						if (order.column > colIndex) {
							order.column = order.column - 1;
						}
					});
				});
			}
		}
	}

	private createNormalisedRequestFilters(
		tableColumnDefinitions: TableColumnDefinition[],
	): DataTableRequestFilter[] {
		return tableColumnDefinitions
			.filter(
				(column) =>
					column.filter &&
					column.filter.operation &&
					this.isColumnVisibleInFilter(column),
			)
			.map((column) => {
				// Process between filter
				const columnFilter = column.filter;
				columnFilter.dateTimeProperties = column.dateTimeProperties;
				if (columnFilter.operation.value === FilterType.BETWEEN) {
					// If only lower value exists, change between filter to greater than filter
					if (columnFilter.value && !columnFilter.upperValue) {
						columnFilter.operation.value = FilterType.GREATER_THAN;
					}
					// If only upper value exists, change between filter to less than filter and assign upper value to lower value
					if (columnFilter.upperValue && !columnFilter.value) {
						columnFilter.operation.value = FilterType.LESS_THAN;
						columnFilter.value = columnFilter.upperValue;
						columnFilter.upperValue = undefined;
					}
				}
				return column;
			})
			.map((column) => ({
				column,
				normalisedFilterValue: column.filter.value
					? String(column.filter.value).trim()
					: '',
				normalisedFilterUpperValue: column.filter.upperValue
					? String(column.filter.upperValue).trim()
					: '',
			}))
			.filter(
				(columnAndFilterValue) =>
					columnAndFilterValue.normalisedFilterValue !== '' ||
					columnAndFilterValue.column.filter.operation.value ===
						FilterType.EMPTY,
			)
			.map((columnAndFilterValue) => {
				// return filter to be used in request
				const columnFilter = columnAndFilterValue.column.filter;

				const requestFilter = {
					data: columnAndFilterValue.column.name,
					value: columnAndFilterValue.normalisedFilterValue,
					upperValue: columnFilter.upperValue
						? columnFilter.upperValue
						: undefined,
					operation: columnFilter.operation.value,
					not: columnFilter.not,
					ignoreCase: columnFilter.ignoreCase,
					dateTimeProperties: columnFilter.dateTimeProperties,
				} as DataTableRequestFilter;

				// The adapter logic must be applied here to avoid the value changes in the filter
				new DataTableRequestFilterDateTimeAdapter(
					columnAndFilterValue.column,
				).updateFilterValues(requestFilter);

				return requestFilter;
			});
	}

	private onDataTableAjaxDataFilter(
		dataTablesResponse: DataTablesResponse,
	): DataTablesResponse {
		this.updateRowDataWithMoreInformation(
			dataTablesResponse.data,
			dataTablesResponse.descriptionMap,
			dataTablesResponse.readOnlyRecords,
		);
		this.updateColumnLabels(dataTablesResponse.columnLabelMap);
		this.transformAjaxResponse(dataTablesResponse);
		return dataTablesResponse;
	}

	private getBaseTableOptions(): DataTables.Settings {
		// NOTE: Jake: 2019-07-19 - WEBUI-2121
		// - only include a-datatable__info div if paging info is true
		// - G = custom <input> to go to a page.
		// https://datatables.net/reference/option/dom
		const reverseOrderDom = this.useAdvancedPagination ? 'J' : '';

		const dom = `
			<"a-datatable"
				<"a-datatable__table"t>
				${
					this.paging
						? `
					<"a-datatable__info"
						p
						` +
						  reverseOrderDom +
						  `
						G
						i
					>
				`
						: ''
				}
			>
		`;
		return {
			colReorder: this.colReorder,
			columns: this.tableColumnSetting,
			pagingType: 'full',
			dom,
			info: this.paging,
			language: this.datatablesTranslations,
			lengthChange: false,
			order: [],
			ordering: this.sortable,
			paging: this.paging,
			scrollY: '', // ie. "200px"
			scrollCollapse: false,
			searching: this.searching,
			footerCallback: (tfoot, data, start, end, display) => {
				if (this.footerCallback) {
					this.footerCallback(tfoot, data, start, end, display);
				}
			},
			drawCallback: (settings) => this.onDataTableDrawCallback(settings),
		};
	}

	/**
	 * toggleSpinner will either show the loading screen for the table if given a true value or
	 * show no loading screen if given false.
	 *
	 * @deprecated This method should only be used by this class internally moving forward. If possible, please replace uses of
	 * `this.tableRef.toggleSpinner` with passing in a value to "is-loading.bind" for acurity-table.
	 * See asset-transaction-list.ts for an example.
	 */
	public toggleSpinner(isSpin: boolean) {
		this.getTableOverlayReference().toggleClass(
			'a-table-overlay--shown',
			isSpin,
		);
	}

	private refreshDataTableInfo() {
		if (this.getDataTable()) {
			// Use the internal function _fnUpdateInfo to refresh the data table info label
			// without redrawing the entire table.
			const internal = <any>(
				this.jqueryTableReference.dataTable.ext.internal
			);
			internal._fnUpdateInfo(this.getDataTable().context[0]);
		}
	}

	private removePaginationLastButton() {
		$(this.element).find('.last.paginate_button').remove();
	}

	public forceInitScrollbarStyling() {
		if (this.perfectScroll) {
			// If already initialized, destroy it and re-create it
			this.perfectScroll.destroy();
			this.perfectScroll = null;
		}
		// Set up PerfectScrollbar and re-attach loading overlay
		const $tableContainer = this.jqueryTableReference.parent().get(0);
		this.perfectScroll = new PerfectScrollbar($tableContainer, {
			wheelPropagation: true,
			useBothWheelAxes: true,
		});

		// NOTE(Jake): 2018-10-03
		// Commented this out as this seemingly does nothing.
		// If this is not the case, uncomment this and replace
		// this comment with the intent of this behaviour.
		// this.getTableOverlayReference()
		// 	.detach()
		// 	.prependTo($table.parent().parent().get(0));
	}

	/**
	 * Clear the filter options
	 */
	public clearFilterOptions(): void {
		Logger.log('Resetting filters');
		const tableColumsHasFilter: TableColumnDefinition[] =
			this.initTableColumnInfoFromTableColumns().filter((c) => c.filter);

		this.setTableFilterOptions(tableColumsHasFilter);

		this.tableColumnInfo.forEach((column: TableColumnDefinition) => {
			if (!this.isColumnVisibleInFilter(column)) {
				return;
			}

			// If filter form tableColumns exists, reset filter to it.
			const filter = tableColumsHasFilter
				.filter((c) => c.name === column.name)
				.map((c) => c.filter)[0];
			if (filter) {
				// should make a copy because filter refer to the same object in this.tableColumns
				// `{...filter}` doesn't work because filter.value may be an array. We need a deep copy.
				column.filter = JSON.parse(JSON.stringify(filter));
				return;
			}

			// 'Not' to false
			column.filter.not = false;

			// If set, 'Operation' is whatever's the first value is
			// ie. "STARTS_WITH", "IS_EQUAL"
			if (column.filter.filterOptions.length > 0) {
				column.filter.operation.value =
					column.filter.filterOptions[0].value;
			}

			// 'Value' and 'Upper value' to  empty
			if (column.codeList) {
				column.filter.value = null;
			} else {
				column.filter.value = '';
			}
			column.filter.upperValue = '';

			// 'Ignore case' to true
			column.filter.ignoreCase = true;
		});
	}

	private addAdditionalTableColumns(columns: DataTablesRequestColumn[]) {
		if (!columns) {
			throw new Error('Cannot pass in empty columns parameter.');
		}
		// add rowIdKey and/or deleted field if not specified in table columns
		const additionalColumnNames = [
			this.rowIdKey,
			this.deletedHighlightField,
		];
		additionalColumnNames
			.filter(
				(additionalColumnName) =>
					additionalColumnName &&
					!columns.some(
						(column) => column.data === additionalColumnName,
					),
			)
			.map((additionalColumnName) =>
				this.createDataTablesRequestColumn({
					data: additionalColumnName,
				}),
			)
			.forEach((additionalColumn) => columns.push(additionalColumn));
	}

	private static renderColumnWithFunc(
		fieldValue: string | number | CodeAndDescription,
		columnInfo: TableColumnDefinition,
	): string | HTMLElement {
		const args = [fieldValue, columnInfo];
		if (columnInfo.args) {
			args.push(...columnInfo.args);
		}
		const r = columnInfo.render(...args);
		return r;
	}

	/**
	 * Set default ordering of columns if no explicit order is requested
	 * @param data Table Data
	 */
	private updateColumnSortOrder(data: DataTablesRequest) {
		if (!data || !data.order) {
			return;
		}

		if (data.order.length > 0) {
			// An explicit order is requested by the user
			if (
				this.secondarySortFields &&
				this.secondarySortFields.length > 0
			) {
				for (const fieldPair of this.secondarySortFields) {
					data.columns.push(
						this.createDataTablesRequestColumn({
							data: fieldPair.secondary,
						}),
					);

					let primaryColumnIndex: number = null;
					for (const columnIndex of Object.keys(data.columns)) {
						if (
							data.columns[columnIndex].data === fieldPair.primary
						) {
							primaryColumnIndex = parseInt(columnIndex, 10);
							break;
						}
					}

					let primaryOrderIndex: number = null;
					if (primaryColumnIndex !== null) {
						for (const orderIndex in data.order) {
							if (
								data.order[orderIndex].column ===
								primaryColumnIndex
							) {
								primaryOrderIndex = parseInt(orderIndex, 10);
								break;
							}
						}
					}

					if (primaryOrderIndex !== null) {
						// The primary column is being ordered, so order the secondary column
						const secondaryOrder: DataTablesRequestOrder = {
							column: data.columns.length - 1,
							dir: data.order[primaryOrderIndex].dir,
						};
						data.order.splice(
							primaryOrderIndex + 1,
							0,
							secondaryOrder,
						);
					}
				}
			}
		} else if (this.multiColumnOrder && this.multiColumnOrder.length > 0) {
			data.order = this.multiColumnOrder
				.map((multiColumnOrderElement) => {
					const columnIndex = data.columns.findIndex(
						(column) =>
							column.data === multiColumnOrderElement.name,
					);
					return {
						column: columnIndex,
						dir: multiColumnOrderElement.direction,
					} as DataTablesRequestOrder;
				})
				.filter((order) => order.column >= 0);
		} else if (this.defaultSortField) {
			// sort by single column (the defaultSortField)
			let defaultSortFieldColumnIndex: number = null;
			for (const i in data.columns) {
				if (data.columns[i].data === this.defaultSortField) {
					defaultSortFieldColumnIndex = parseInt(i, 10);
					break;
				}
			}
			if (defaultSortFieldColumnIndex === null) {
				// We need to add the column so we can refer to it
				data.columns.push(
					this.createDataTablesRequestColumn({
						data: this.defaultSortField,
					}),
				);
				defaultSortFieldColumnIndex = data.columns.length - 1;
			}
			data.order[0] = {
				column: defaultSortFieldColumnIndex,
				dir: this.defaultSortDirection,
			};
		}
	}

	private isCodeListLoadedWithData(columnInfoName: string): boolean {
		for (const fieldType of fieldTypes) {
			if (fieldType.matches(columnInfoName)) {
				return true;
			}
		}
		return false;
	}

	// Column Styling
	private initColumnHeaderIcons(): void {
		this.onTableInitialised().then(() => {
			// $table.find('thead th.i-sortable').append('<i class="fas fa-sort"></i>');
			// need to set id of th elements so we can match applied filters to corresponding table heading
			if (this.tableColumns) {
				for (let i = 0; i < this.tableColumns.length; i++) {
					const column = this.tableColumns[i];
					this.jqueryTableReference
						.find('thead th.i-sortable')
						.eq(i)
						.attr('id', 'th-' + column.name);
				}
			}
		});
	}

	private setupSorting(): void {
		// Show number next to filter icon to show order of multi column sort
		const dataTable = this.getDataTable();
		if (!dataTable) {
			return;
		}
		const tableDataSet = dataTable.data();
		tableDataSet.order().forEach((e, i) => {
			const sortedColumn: JQuery<HTMLElement> = this.jqueryTableReference
				.find(`thead th`)
				.eq(Number(e[0]));
			if (
				sortedColumn.hasClass('sorting_asc') ||
				sortedColumn.hasClass('sorting_desc')
			) {
				sortedColumn.attr('data-sort-order', i + 1);
			} else {
				sortedColumn.removeAttr('data-sort-order');
			}
		});
	}

	/**
	 * onCloseContextMenu is callback fired from <context-menu>
	 */
	private onCloseContextMenu = (): void => {
		this.hideContextMenu();
	};

	// Context Menu
	private initContextMenuEvents() {
		if (!this.contextMenu) {
			return;
		}

		// init contextual menu
		// Left click event handler
		this.jqueryTableReference
			.find('.a-table__context-menu-btn')
			.off('click')
			.on('click', (event) => {
				event.stopPropagation();
				const dataTable = this.getDataTable();
				if (!dataTable) {
					return;
				}
				this.setSelected(
					$(event.currentTarget).parents('tr'),
					dataTable.page.info().page,
				);

				const isContextMenuOpen: boolean = Boolean(this.contextMenuPos);
				if (isContextMenuOpen) {
					this.hideContextMenu();
				} else {
					this.showContextMenu(event);
				}
			});

		// Right click event handler for table row
		this.jqueryTableReference.find('tbody tr').each((_, el) => {
			const id = $(el).attr('id');
			if (!id) {
				return;
			}
			$(el)
				.off('contextmenu')
				.on('contextmenu', (event) => {
					event.preventDefault();
					event.stopPropagation();

					const dataTable = this.getDataTable();
					if (!dataTable) {
						return;
					}
					this.setSelected(
						$(event.currentTarget),
						dataTable.page.info().page,
					);
					this.showContextMenu(event);
				});
		});

		this.publishEvent(AcurityTable.Events.InitContextMenuEvents, {
			source: this.tableId,
		});

		// Right click event handler for body (hides menu and reenables table)
		$(document).off('contextmenu').on('contextmenu', this.hideContextMenu);
	}

	private showContextMenu(e: {
		target: HTMLElement;
		which: number;
		clientX: number;
		clientY: number;
	}) {
		const id = Number(e.target.closest('tr').getAttribute('id'));
		if (!id) {
			throw new Error('Expected tr element to have an id attribute.');
		}

		this.rowId = id;
		this.selectedColumnIndex = [...e.target.parentElement.children].indexOf(
			e.target,
		);

		if (!this.isContextMenuEnabledForRow(this.selectedObj)) {
			// Early exit if this row doesn't have a context menu
			return;
		}

		let x: number;
		let y: number;
		// If triggered by right mouse (3), open menu right next to mouse
		// Else, open menu next to the button trigger [...]
		if (e.which === 3) {
			x = e.clientX;
			y = e.clientY;
		} else {
			const rect = e.target.getBoundingClientRect();
			x = rect.left + rect.width - 10;
			y = rect.top + rect.height - 10;
		}
		if (
			this.contextMenuPos &&
			this.contextMenuPos.x === x &&
			this.contextMenuPos.y === y &&
			this.rowId === id
		) {
			// Avoid creating a new JS object and flowing state if no change.
			// We must do this to avoid React re-rendering constantly in a loop.
			return;
		}
		this.contextMenuPos = {
			x,
			y,
		};

		this.publishEvent(AcurityTable.Events.ShowContextMenu, {
			source: this.tableId,
			table: this,
			rowId: this.rowId,
		});
	}

	private hideContextMenu = (): void => {
		if (this.contextMenuPos === undefined) {
			return;
		}
		this.contextMenuPos = undefined;
		// NOTE: Jake: 2019-11-12 - WEBUI-2690
		// Nothing seems to actually utilize this, at least not yet.
		this.publishEvent(AcurityTable.Events.HideContextMenu, {
			source: this.tableId,
			table: this,
		});
	};

	/**
	 * Is the context menu enabled for the row?
	 */
	protected isContextMenuEnabledForRow = (
		rowData: AcurityTableRecord,
	): boolean => {
		// By default, the menu is enabled. Subtypes may override.
		return true;
	};

	public copyTableRows = (): void => {
		const columnsVisibleInTable: DataTables.ColumnSettings[] =
			this.getColumnsVisibleInTable();
		const tableHeader: string = columnsVisibleInTable
			.reduce((acc, column) => acc + column.title + '\t', '')
			.trim();
		const tableColumnFieldNames: string[] = columnsVisibleInTable.map(
			(column) => column.data as string,
		);

		let tableBody: string = '';
		this.getAjaxTableDataSet().forEach((row) => {
			tableBody +=
				tableColumnFieldNames
					.reduce((acc, fieldName) => {
						let value: string = '';
						if (
							FormModelUtils.isFieldTypeNumeric(fieldName) &&
							!row[fieldName]
						) {
							value = '0';
						} else if (row[fieldName]) {
							value = String(row[fieldName]).trim();
						}
						return acc + value + '\t';
					}, '')
					.trim() + '\n';
		});

		Clipboard.copy(tableHeader + '\n' + tableBody);
	};

	public copySelectedCell = (): void => {
		const tableColumnFieldNames: string[] =
			this.getColumnsVisibleInTable().map(
				(column) => column.data as string,
			);
		const selectedCellFieldName: string = this.contextMenu
			? tableColumnFieldNames[this.selectedColumnIndex - 1]
			: tableColumnFieldNames[this.selectedColumnIndex];

		if (!selectedCellFieldName) {
			return;
		}

		const selectedRow = this.getAjaxTableDataSet().find(
			(row) => row.DT_RowId === this.rowId,
		);
		Clipboard.copy(String(selectedRow[selectedCellFieldName]).trim());
	};

	public setListeners(listener: RowListener): void {
		if (this.getDataTable()) {
			throw new Error(
				'Cannot call setListeners() if DataTable is already initialized. This is to avoid triggering two AJAX requests.',
			);
		}
		if (this.clickListener !== listener) {
			this.clickListener = listener;
		}
	}

	private setFocusListener(tableRow: JQuery) {
		const aureliaTableRef = this;
		if (tableRow.attr('id') != null) {
			tableRow[0].tabIndex = 0;
			tableRow.get(0).addEventListener('focus', () => {
				const dataTable = this.getDataTable();
				if (!dataTable) {
					return;
				}
				aureliaTableRef.setSelected(
					tableRow,
					dataTable.page.info().page,
				);
			});
		}
	}

	// Selection
	private setSelected(row: JQuery<HTMLElement> | undefined, page) {
		if (!row || row.length === 0) {
			return;
		}
		if (
			!this.currentSelection ||
			this.currentSelection.row === -1 ||
			this.currentSelection.row?.get(0).id !== row?.get(0).id
		) {
			this.clearTableSelection();
			this.currentSelection = {
				row,
				page,
			};
			this.selectedObjId = String(row.get(0).id);
		}
		if (!row.hasClass('selected')) {
			row.addClass('selected');
		}

		if (!this.isContextMenuEnabledForRow(this.selectedObj)) {
			// Hide the menu if it is not available for this row and was opened for another row
			this.hideContextMenu();
		}
	}

	public clearTableSelection() {
		this.contextMenuItemConfigs = [];
		const dataTable = this.getDataTable();
		if (dataTable) {
			dataTable.$('tr.selected').removeClass('selected');
		}
		this.currentSelection = { row: -1, page: -1 };
		this.selectedObjId = null;
	}

	public openSelected(): void {
		if (!this.clickListener) {
			return;
		}
		// NOTE: Jake: 2018-12-07 - WEBUI-1284
		// For "payroll-details" URL, it should select the first record in the table
		// if there is only 1 record. It should also open the slideout panel automatically.
		const record = this.selectedObj;
		if (record === undefined) {
			throw new Error('Unexpected error. No record selected');
		}
		this.clickListener(record);
	}

	// Navigation
	public selectSpecificRecord(
		recordIdentifier: RecordIdentifier,
	): Promise<JQuery> {
		return new Promise<JQuery>((resolve) => {
			const tableRef = this.getDataTable();
			if (!tableRef) {
				throw new Error(
					'Cannot do "selectSpecificRecord" as table is not yet rendered',
				);
			}

			let specificPage = 0;
			{
				let pages = 1;
				if (tableRef) {
					specificPage = tableRef.page(); // default to current page
					pages = tableRef.page.info().pages;
				}

				let specificRow: undefined | JQuery<HTMLElement>;
				switch (recordIdentifier) {
					case RecordIdentifier.FIRST:
						specificPage = 0;
						break;
					case RecordIdentifier.LAST:
						specificPage = pages - 1;
						break;
					case RecordIdentifier.NEXT:
						specificRow = tableRef.$('tr').first();
						break;
					case RecordIdentifier.PREVIOUS:
						specificRow = tableRef.$('tr').last();
						break;
				}

				// navigating to next/previous record but no rows selected so start at the top/bottom
				if (
					(recordIdentifier === RecordIdentifier.NEXT ||
						recordIdentifier === RecordIdentifier.PREVIOUS) &&
					!(this.currentSelection.row.length > 0)
				) {
					this.setSelected(specificRow, specificPage);
					resolve(specificRow);
					return;
				}
			}

			// If we are on the specified page, resolves immediately. Otherwise waits until page is ready.
			{
				let pageLimit;
				let rowLimit;
				let pageParam;
				let specificRow: JQuery | null = null;

				// need to repeat switch because table page has changed
				switch (recordIdentifier) {
					case RecordIdentifier.FIRST:
						specificRow = tableRef.$('tr').first();
						break;
					case RecordIdentifier.LAST:
						specificRow = tableRef.$('tr').last();
						break;
					case RecordIdentifier.NEXT: {
						pageLimit = tableRef.page.info().pages - 1;
						rowLimit = tableRef.data().length - 1;
						const currentRow = this.currentRowGetter();
						if (currentRow) {
							specificRow = currentRow.next();
						}
						pageParam = 'next';
						break;
					}
					case RecordIdentifier.PREVIOUS: {
						pageLimit = 0;
						rowLimit = 0;
						const currentRow = this.currentRowGetter();
						if (currentRow) {
							specificRow = currentRow.prev();
						}
						pageParam = 'previous';
						break;
					}
				}

				// navigating to next/previous record but reached page and row limit, stop navigating
				if (
					(recordIdentifier === RecordIdentifier.NEXT ||
						recordIdentifier === RecordIdentifier.PREVIOUS) &&
					specificPage === pageLimit &&
					this.currentSelection.row[0]._DT_RowIndex === rowLimit
				) {
					return;
				}

				// navigating to next/previous record but reached end of page, need to load the next/prev page
				if (
					(recordIdentifier === RecordIdentifier.NEXT ||
						recordIdentifier === RecordIdentifier.PREVIOUS) &&
					(!specificRow || specificRow.length === 0)
				) {
					// NOTE: Jake: 2019-07-17 - WEBUI-2246
					// This used to be 'processing.dt' but that doesn't fire on audit-dialog's as
					// the DataTable used on those modals has pre-loaded data.
					tableRef.one('draw.dt', () => {
						let pageSpecificRow: JQuery;
						// NOTE: Jake: 2019-07-17 - WEBUI-2246
						// For audit-dialog / pre-loaded data, we must query with "tr:visible" or this
						// will not work as expected.
						switch (recordIdentifier) {
							case RecordIdentifier.NEXT:
								pageSpecificRow = tableRef
									.$('tr:visible')
									.first();
								break;
							case RecordIdentifier.PREVIOUS:
								pageSpecificRow = tableRef
									.$('tr:visible')
									.last();
								break;
							default:
								throw new Error(
									'Invalid record identifier given. Expected NEXT or PREVIOUS',
								);
						}
						pageSpecificRow.addClass('selected');
						this.setSelected(pageSpecificRow, specificPage);
						resolve(pageSpecificRow);
						return;
					});
					tableRef.page(pageParam).draw('page');
				} else {
					this.setSelected(specificRow, specificPage);
					resolve(specificRow);
					return;
				}
			}
		});
	}

	/**
	 * Move to specific page
	 */
	public selectSpecificPage(pageIdentifier: PageIdentifier): Promise<JQuery> {
		return new Promise<JQuery>((resolve, reject) => {
			const dataTable = this.getDataTable();
			if (!dataTable) {
				reject(
					'Cannot do "selectSpecificPage" as table is not yet rendered',
				);
				return;
			}

			const currentPage = dataTable.page();
			const totalPages = dataTable.page.info().pages;
			if (
				((pageIdentifier === PageIdentifier.PREVIOUS ||
					pageIdentifier === PageIdentifier.FIRST) &&
					currentPage === 0) ||
				((pageIdentifier === PageIdentifier.NEXT ||
					pageIdentifier === PageIdentifier.LAST) &&
					currentPage + 1 >= totalPages)
			) {
				Logger.log(
					`#${this.getTableReference().attr('id')}:`,
					`Already on the ${
						pageIdentifier === PageIdentifier.NEXT
							? 'last'
							: 'first'
					} page.`,
					`Selecting the ${
						pageIdentifier === PageIdentifier.NEXT
							? 'last'
							: 'first'
					} record.`,
				);
				let rowToBeSelected: JQuery;
				if (
					pageIdentifier === PageIdentifier.PREVIOUS ||
					pageIdentifier === PageIdentifier.FIRST
				) {
					rowToBeSelected = dataTable.$('tr').first();
				} else if (
					pageIdentifier === PageIdentifier.NEXT ||
					pageIdentifier === PageIdentifier.LAST
				) {
					rowToBeSelected = dataTable.$('tr').last();
				}
				if (rowToBeSelected) {
					this.setSelected(rowToBeSelected, currentPage);
				}
				resolve(rowToBeSelected);
				return;
			} else {
				// NOTE: Jake: 2019-07-17 - WEBUI-2246
				// This used to be 'processing.dt' but that doesn't fire on audit-dialog's as
				// the DataTable used on those modals has pre-loaded data.
				dataTable.one('draw.dt', () => {
					Logger.log(
						`#${this.getTableReference().attr('id')}:`,
						`Navigating to the ${pageIdentifier} page.`,
					);
					let rowToBeSelected: JQuery;
					if (
						pageIdentifier === PageIdentifier.PREVIOUS ||
						pageIdentifier === PageIdentifier.LAST
					) {
						rowToBeSelected = dataTable.$('tr:visible').last();
					} else if (
						pageIdentifier === PageIdentifier.NEXT ||
						pageIdentifier === PageIdentifier.FIRST
					) {
						rowToBeSelected = dataTable.$('tr:visible').first();
					}
					if (rowToBeSelected) {
						this.setSelected(rowToBeSelected, dataTable.page());
					}
					resolve(rowToBeSelected);
					return;
				});
				dataTable.page(pageIdentifier).draw('page');
			}
		}).catch((e) => {
			throw new Error(e);
		});
	}

	// Refreshing Tables
	public refreshNormalTable(
		dataSet: AcurityTableRecord[],
		descriptionMap?: { [fieldName: string]: DescriptionDefinition },
	): void {
		// for normal table reset data
		if (this.isDataTable()) {
			this.updateRowDataWithMoreInformation(dataSet, descriptionMap);
		}
		this.tableDataSet = dataSet;
		if (!this.isDataTable()) {
			this.initNormalTableData(descriptionMap);
			this.initNormalTableOptions();
			this.calculatePageLengthForFillTable();
		}
		this.forceInitScrollbarStyling();
	}

	public clearNormalTable() {
		this.tableDataSet = [];
	}

	public refreshAjaxTable(resetPaging: boolean = true) {
		const dataTable = this.getDataTable();
		if (!dataTable) {
			return;
		}
		dataTable.ajax.reload(() => {
			this.updateSelectedRow();
		}, resetPaging);
	}

	private readonly updateSelectedRow = (): void => {
		let recordId: number | string;
		if (this.currentRowGetter() != null) {
			recordId = parseInt(this.currentRowGetter().attr('id'), 10);
		} else {
			recordId = Number(this.selectedObjId);
			if (isNaN(recordId)) {
				recordId = this.selectedObjId;
			}
		}
		if (recordId !== null && recordId !== undefined) {
			this.setSelectedRecordId(recordId);
		}
	};

	/**
	 * recordId is a number when used in classic form, but it is a string in acurity table internally.
	 */
	public setSelectedRecordId(recordId: number | string) {
		const dataTable = this.getDataTable();
		if (!dataTable) {
			Logger.warning(
				"setSelectedRecordId should not be called if DataTable() isn't initialized.",
			);
			return;
		}
		const tableRef = this.getTableReference();
		const escapedRowId = $.escapeSelector(recordId.toString());
		const selectedRow = tableRef.find('tbody tr#' + escapedRowId);
		this.setSelected(selectedRow, dataTable.page.info().page);
		if (selectedRow.length === 0) {
			this.selectedObjId = String(recordId);
		}
	}

	@computedFrom('selectedObjId', 'rowIdKey')
	public get selectedObj(): { [fieldName: string]: any } | undefined {
		if (!this.rowIdKey) {
			throw new Error(
				'No rowIdKey is configured on <acurity-table>. Cannot use "selectedObj".',
			);
		}
		const dataTable = this.getDataTable();
		if (!dataTable) {
			// Logger.warning('Cannot get selectedObj if table is destroyed.');
			return undefined;
		}
		const tableDataSet = dataTable.data(); // (data.table as AcurityTable).getDataTable().data();
		if (!tableDataSet || tableDataSet.length === 0) {
			Logger.warning('Cannot get selectedObj if table has no data.');
			return undefined;
		}
		Logger.warning('Try to find ', this.rowIdKey, ' in dataset.');
		const rowId = this.selectedObjId;
		for (const rowKey of Object.keys(tableDataSet)) {
			const row = tableDataSet[rowKey];
			const id = String(ObjectUtils.getProperty(row, this.rowIdKey));
			if (id !== rowId) {
				continue;
			}
			return row;
		}
		Logger.warning(
			'Unable to find ',
			this.rowIdKey,
			' in dataset. Is that column being returned?',
		);
		return undefined;
	}

	// Exporting Table Contents
	public normalTableExport(filename: string, timestampFormat?: string) {
		const dataTable = this.getDataTable();
		if (!dataTable) {
			return;
		}
		const data: Array<{ [prop: string]: any }> = [];
		dataTable.data().each((o) => {
			const obj = {};
			for (const i of Object.keys(this.tableColumns)) {
				const name = this.tableColumns[i].name;
				const label = this.tableColumns[i].label;

				let value = o[name];
				if (
					timestampFormat &&
					DateFormatValueConverter.isValidApiFormat(value)
				) {
					value = Converters.dateFormat(value, timestampFormat);
				}
				obj[label] = value;
			}
			data.push(obj);
		});
		Export.exportToCSV(filename + '.csv', data);
	}

	/**
	 * Downloads a CSV file made from an array of objects that are received from the dataGetter parameter.
	 * It returns a promise that the download has started.
	 * @param {string} filename - The file will have this name with the file extension ".csv"
	 * @param {(tableParmas: APIParameters) => Promise<Array<Object>>} dataGetter - This will get the data using the
	 * parameters that DataTables used last.
	 * @returns {Promise<void>}
	 */
	public async ajaxTableExport(
		filename: string,
		dataGetter: (
			tableParmas: APIParameters,
		) => Promise<Array<{ [prop: string]: any }>>,
	) {
		const data = await dataGetter(this.getDataTable().ajax.params());
		/* if (tableColumnLength !== Object.keys(data[0]).length) {
			throw {
				error: this.i18n.tr('errors.csv-headers-being-returned-are') + ' ' +
					Object.keys(data[0]).reduce((str, key) => str + `"${key}"` + ', ', ''),
				status: '',
			};
		 }*/
		Export.exportToCSV(filename + '.csv', data);
	}

	public getExportDialogModel(): ExcelExportDialogModel {
		return {
			...ExcelExportDialogModelDefaults,
			numberToExport:
				!this.recordsTotal || this.recordsTotal > this.exportResultSize
					? this.exportResultSize
					: this.recordsTotal,
			recordsTotal: this.recordsTotal,
			configLimit: this.settingsState.state.exportToCSVRowLimit,
		};
	}

	public validateNumberToExport(numberToExport: number): number {
		return !isNaN(numberToExport) && Number(numberToExport) > 0
			? Number(numberToExport)
			: this.exportResultSize;
	}

	public getColumnsToExport(): ExportColumn {
		const columnsToExport: ExportColumn = {};
		const exportableColumns: TableColumnDefinition[] =
			this.tableColumnInfo.filter((column) => {
				return (
					column.name &&
					column.name.substring(0, 2) !== '__' &&
					!AcurityTable.isHiddenIn(column, HiddenTarget.EXPORT)
				);
			});

		exportableColumns.forEach((column) => {
			columnsToExport[column.name] = column.label;
		});

		return columnsToExport;
	}

	private updateRowDataWithMoreInformation(
		dataSet: AcurityTableRecord[],
		descriptionMap?: { [fieldName: string]: DescriptionDefinition },
		readOnlyRecords?: number[],
	) {
		// NOTE: Jake: 2018-10-18 - WEBUI-1092
		// This is utilized by renderColumn() to add additional text to short codes.
		this.descriptionMap = descriptionMap;

		// This is currently used by Pension Summary to set certain records as read only.
		this._readOnlyRecords = readOnlyRecords;

		// NOTE: Jake: 2019-10-21 - WEBUI-2486
		// Add deleted field to the filter panel
		if (this.deletedHighlightField) {
			let hasDeletedFieldDefined = false;
			for (const tableColumn of this.tableColumnInfo) {
				hasDeletedFieldDefined =
					hasDeletedFieldDefined ||
					tableColumn.name === this.deletedHighlightField;
			}
			if (!hasDeletedFieldDefined) {
				this.tableColumnInfo.push({
					name: this.deletedHighlightField,
					label: 'Deleted',
					dataType: { name: DataTypeEnum.Character },
					filter: createFilterModel(),
					hiddenFrom: [HiddenTarget.TABLE],
				});
			}
		}

		// NOTE: Jake: 2018-10-22 - WEBUI-1105
		// Apply codeMap data to each column info item. This data is utilized
		// by filter.ts and allows users to select from a dropdown to search by
		// a specific code value.
		Logger.log('Setting code lists and filter options');
		this.tableColumnInfo.forEach((column) => {
			const columnName = AcurityTable.getNameFromKey(column.name);
			const codeMap =
				this.descriptionMap &&
				this.descriptionMap[columnName] &&
				this.descriptionMap[columnName].codeMap
					? this.descriptionMap[columnName].codeMap
					: null;
			// do not add codeMap to filter if column has a fieldType
			if (codeMap !== null && !column.isCodeListLoadedWithData) {
				// convert codeMap to codeList. separate jira WEBUI-3267 raised to
				// refactor codeLists to codeMaps on FieldModel and ColumnDefinition
				const codeList: CodeAndDescription[] = [];
				for (const code of Object.keys(codeMap)) {
					codeList.push({
						code,
						description: codeMap[code].description,
					});
				}
				column.codeList = codeList;
			}

			// reset filter options for columns with code lists
			if (column.filter && column.codeList) {
				column.filter.filterOptions = [];
				filterOptions.forEach((filterOption: FilterOption) => {
					switch (filterOption.value) {
						case FilterType.IN:
						case FilterType.EMPTY:
							column.filter.filterOptions.push(filterOption);
							break;
					}
				});
			}
		});

		if (dataSet) {
			this.rowId = 1;
			dataSet.forEach((data) => {
				// NOTE(Jae): 2020-04-27
				// We use "result()" here so we can get nested table columns, like the ones
				// provided by 'ato-interfaces', ie. "atoRequest.TQi_Identity"
				data.DT_RowId = this.rowIdKey
					? result(data, this.rowIdKey)
					: this.rowId++;
				if (DEVELOPMENT && data.DT_RowId === undefined) {
					const message =
						'Invalid rowId computed for dataset. See console.';
					Logger.error(message, dataSet);
					throw new Error(message);
				}
				if (this.contextMenu) {
					data.__contextMenu = this.getContextMenuHTML(data);
				}
			});
		}
	}

	private updateColumnLabels(columnLabelMap: {
		[columnName: string]: string;
	}): void {
		if (
			!this.customColumns &&
			columnLabelMap &&
			Object.keys(columnLabelMap).length > 0
		) {
			// update table column headers
			// Need to use JQuery here because columnLabelMap is only ready after the ajax table data has loaded.
			// By that time, the DataTable has already been initialised with its options and set the column headers.
			const dataTable = this.getDataTable();
			if (dataTable) {
				for (
					let colIndex = 0;
					colIndex < this.tableColumnSetting.length;
					colIndex++
				) {
					const reorderedIndex =
						dataTable.colReorder.transpose(colIndex);
					const $headers: JQuery = $(
						dataTable.column(reorderedIndex).header(),
					);
					if ($headers.length === 0) {
						throw new Error(
							'Unable to retrieve <th> element to set column label',
						);
					}
					const columnLabel =
						columnLabelMap[
							this.tableColumnSetting[colIndex].data as string
						];
					if (columnLabel) {
						$headers.text(columnLabel);
					}
				}
			}
			// update filter column labels
			this.tableColumnInfo.forEach((column) => {
				if (columnLabelMap[column.name]) {
					column.label = columnLabelMap[column.name];
				}
			});
		}
	}

	private static getNameFromKey(name: string): string {
		const parts = name.split('.');
		return parts[parts.length - 1];
	}

	protected renderColumnClassName(
		columnInfo: TableColumnDefinition,
		sortable: boolean,
	): string {
		let className: string = 'text-left';
		if (columnInfo.dataType) {
			switch (columnInfo.dataType.name) {
				case 'Currency':
				case 'BigDecimal':
				case 'Integer':
				case 'Long':
				case 'Percentage':
					className = 'text-right';
					break;
			}
		}
		className += sortable ? ' i-sortable expand' : '';
		return className;
	}

	// NOTE(Jake): 2018-10-18 - WEBUI-1092
	// Anytime I made each param go to a seperate line, running `yarn nps tslintfix` would
	// convert tabs to spaces, causes the build to fail, so I'm ignoring the linting rule.
	private renderColumn(
		fieldValue: string | number | CodeAndDescription | undefined | null,
		dataType: 'display',
		row: object,
		columnInfo: TableColumnDefinition,
	): string {
		fieldValue = AcurityTable.applyDataTypeFormatting(
			fieldValue,
			columnInfo,
			row,
		);
		// columns that use functions to apply formatting should escape html in their own field values
		if (!columnInfo.render) {
			fieldValue = fieldValue.escapeHtml();
		}

		const newKey = AcurityTable.getNameFromKey(columnInfo.name);
		if (this.isLocalColumn(newKey) && !row[newKey]) {
			row[newKey] = fieldValue;
		}

		let renderedFieldValue: string = this.applyFieldDescription(
			fieldValue,
			row,
			columnInfo,
			newKey,
		);

		// NOTE: Jake: 2018-10-18 - WEBUI-1092
		// Apply an icon before the value.
		// This is used by the "Request Status" column on the ATO Interfaces page.
		// This column is so special that it has an icon, code description and sometimes a html anchor.
		// To properly display icon, we only want first char of field value as the rest of the value is html.
		// This also means description is manually set in formatStatus method of ato-interfaces.ts

		const iconMap = this.iconMap;
		if (iconMap && iconMap[newKey]) {
			const icon = iconMap[newKey][fieldValue.substr(0, 1)];
			renderedFieldValue = icon + ' ' + renderedFieldValue;
		}
		return renderedFieldValue;
	}

	private applyFieldDescription(
		fieldValue: string,
		row: object,
		columnInfo: TableColumnDefinition,
		newKey: string,
	): string {
		// NOTE: Jake: 2018-10-18 - WEBUI-1092
		// Append a description of a code.
		// ie. "P" becomes "P (Pending)"
		const descriptionMap = this.descriptionMap;
		if (
			!columnInfo.isDisableColumnDescription &&
			descriptionMap &&
			descriptionMap[newKey]
		) {
			const codeMap: Map<string, DescriptionForCode> =
				descriptionMap[newKey].codeMap;
			if (!codeMap) {
				return fieldValue;
			}

			const codeFields: CodeFieldDefinition[] =
				descriptionMap[newKey].codeFields &&
				descriptionMap[newKey].codeFields.length > 0
					? descriptionMap[newKey].codeFields.slice()
					: [{ name: columnInfo.name, length: 0 }];

			// NOTE: Lucas: 2019-07-19 - WEBUI-2282
			// Some codes in the same list may be constructed with a different number of field values.
			// However the list of fields a code can be constructed with is always the first elements of the codeFields
			// variable.
			// Eg. if the list of names in codeFields is 'THz_Transaction' then 'THz_Inv_Mnr_code', then a code must be
			// constructed with either the 'THz_Transaction' field then the 'THz_Inv_Mnr_code' field
			// or just the 'THz_Transaction' field.
			while (codeFields.length > 0) {
				// NOTE: Albert: WEBUI-836 2020-01-23 - candidateCode needs trimming because the length
				// of the value of a code field can vary for the same field. eg BSz_Exit_Reason can have
				// values of 'DR' or 'Z'. Without trimming, we only get descriptions for 'DR' record
				const candidateCode: string = codeFields
					.map((codeDef) => {
						const codeDefCellValue = ObjectUtils.getProperty(
							row,
							codeDef.name,
						);
						// WEBUI-4332: Previously, every time we tried to set a description on a table cell that does not contain a value,
						// we logged an error. This is excessive because null values are a normal scenario. In these cases, code values are
						// set to an empty string and descriptions are not applied at all.
						// if (
						// 	codeDefCellValue === null ||
						// 	codeDefCellValue === undefined
						// ) {
						// 	Logger.error(
						// 		'Column "' +
						// 			codeDef.name +
						// 			'" does not exist on row',
						// 	);
						// }
						const value: string = codeDefCellValue
							? codeDefCellValue.toString()
							: '';
						return StringUtils.padEnd(value, codeDef.length, ' ');
					})
					.reduce((a, b) => a + b)
					.trim();

				const descriptionForCode: DescriptionForCode =
					codeMap[candidateCode];
				if (
					descriptionForCode &&
					descriptionForCode.description.trim() !== ''
				) {
					return (
						fieldValue +
						' (' +
						descriptionForCode.description.escapeHtml() +
						')'
					);
				}
				codeFields.pop();
			}
		}

		return fieldValue;
	}

	public static applyDataTypeFormatting(
		fieldValue: string | number | CodeAndDescription,
		columnInfo: TableColumnDefinition,
		row: object,
	): string {
		if (columnInfo.render) {
			// NOTE: Jake: 2018-10-23 - WEBUI-1092 / WEBUI-1110
			// - WEBUI-1092: Used by Ato Interfaces "Request Type" field (which is an object).
			// - WEBUI-1110: Used by DataCompareTab to make a booleans appear blank.
			const args = [];
			if (columnInfo.args) {
				args.push(...columnInfo.args);
			}
			args.push(fieldValue, columnInfo, row);
			const value = columnInfo.render(...args);
			if (value instanceof HTMLElement) {
				return value.outerHTML;
			}
			return value;
		}

		// NOTE: Jake: 2018-11-28 - WEBUI-1220
		// This used to simply be "!fieldValue" however this made
		// "0" dollar prices become blank in table columns.
		if (fieldValue !== 0 && !fieldValue) {
			fieldValue = '';
		}

		const fieldValueStr = String(fieldValue);

		if (typeof fieldValue === 'object') {
			// NOTE: Jake: 2018-10-26 - WEBUI-1108
			// The logic within this if-statement is a bit too "magic". We might want to refactor
			// this so that other components use the "columnInfo.render" logic to render these cases.
			if (fieldValue.code && fieldValue.description) {
				// NOTE: Jake: 2018-10-25 - WEBUI-1108
				// This is used by the Insurance table on the Client Summary page.
				// http://localhost:9000/#/client-summary?client=195155486&fund=GSUP&member=E16798
				return Converters.convertWith(
					ConverterTypes.CODE_DESCRIPTION,
					fieldValue,
				);
			}
			if (fieldValueStr === '[object Object]') {
				throw new Error(
					columnInfo.name +
						' is missing logic to transform the given object into plain text.',
				);
			}
		}

		// NOTE: Jake: 2018-10-25 - WEBUI-1105
		// After fixing some codeMap/codeList bugs this codepath worked again, however
		// it broke icons for ATO Interfaces in "Request Status" and I'm not sure what it's
		// meant to do, so I'm going to leave this commented out for now.
		// if (typeof fieldValue === 'string' &&
		// 	columnInfo.codeList) {
		// 	const fieldValueStr = fieldValue;
		// 	const matchingCodeAndDescription = columnInfo.codeList.find(codeAndDescription => codeAndDescription.code === fieldValueStr.trim());
		// 	if (matchingCodeAndDescription) {
		// 	 	const r = Converters.convertWith(ConverterTypes.CODE_DESCRIPTION, matchingCodeAndDescription, columnInfo.convertTo);
		// 	 	return r;
		// 	}
		// }

		// set formatting of currency and date values in table if their dataType is set in the forms async.html file
		if (columnInfo.dataType) {
			switch (columnInfo.dataType.name) {
				case 'Currency':
					return Converters.currencyFormat(
						fieldValueStr,
						columnInfo.dataType.scale,
					);
				case 'BigDecimal':
				case 'Integer':
					return Converters.numberFormat(
						fieldValueStr,
						columnInfo.dataType.scale,
					);
				case 'Date':
					return Converters.dateFormat(fieldValueStr);
				case 'Timestamp':
					if (columnInfo) {
						/*
						 NOTE: `columnInfo.format` must be checked before columnInfo.dateTimeProperties?.format
						 It is required for `/ato-interface` `ATO Received` field, which the table column format
						 doesn't match with the filter format.
						 */
						const format =
							columnInfo.format ||
							columnInfo.dateTimeProperties?.format;
						return Converters.dateFormat(fieldValueStr, format);
					}
					return Converters.dateFormat(fieldValueStr);
				case 'Percentage':
					return Converters.percentFormat(
						fieldValueStr,
						columnInfo.dataType.scale,
					);
			}
		}

		return fieldValueStr;
	}

	// ---------------------------------------------------------------------------------------------
	// Table v2 methods below
	// ---------------------------------------------------------------------------------------------

	public isColumnVisibleInFilter(column: TableColumnDefinition): boolean {
		return (
			(!column.filter || !column.filter.hidden) &&
			!AcurityTable.isHiddenIn(column, HiddenTarget.FILTER) &&
			!this.isLocalColumn(column.name)
		);
	}

	private getColumnsVisibleInTable(): DataTables.ColumnSettings[] {
		return this.tableColumnOrder
			.map((index) => this.tableColumnSetting[index])
			.filter((column) => column.visible);
	}

	private static isHiddenIn(
		column: TableColumnDefinition,
		hiddenType: HiddenTarget,
	): boolean {
		// we treat empty hiddenFrom as hidden everywhere to make declaring hidden columns simpler
		return (
			column.hiddenFrom &&
			(column.hiddenFrom.length === 0 ||
				column.hiddenFrom.includes(hiddenType))
		);
	}

	// At time of writing "TransactionCentre" and "Reporting Centre" is using the '__' rule.
	private isLocalColumn(columnName: string): boolean {
		return (
			columnName.startsWith('__') ||
			Boolean(
				this.tableColumns?.find((column) => column.name === columnName)
					?.localField,
			)
		);
	}

	private initTableColumnInfoFromTableColumns(): TableColumnDefinition[] {
		const tableColumnInfo = [];
		if (this.tableColumns) {
			for (const userColumn of this.tableColumns) {
				const name = AcurityTable.getNameFromKey(userColumn.name);
				let serverColumn;
				if (this.defaultColumnInfo) {
					serverColumn = this.defaultColumnInfo[name];
				}

				// overrides the default properties with the user properties
				const updatedColumnProperties = {
					...serverColumn,
					...userColumn,
				};
				tableColumnInfo.push(updatedColumnProperties);
			}
		} else if (this.defaultColumnInfo) {
			// showing default columns if possible without an order....
			for (const column of Object.keys(this.defaultColumnInfo)) {
				tableColumnInfo.push(this.defaultColumnInfo[column]);
			}
		}

		if (this.frontEndTableColumnsOverrides) {
			tableColumnInfo
				.filter(
					(columnInfo) =>
						this.frontEndTableColumnsOverrides[columnInfo.name],
				)
				.forEach((columnInfo) => {
					const override =
						this.frontEndTableColumnsOverrides[columnInfo.name];
					for (const prop in override) {
						if (!override.hasOwnProperty(prop)) {
							continue;
						}
						columnInfo[prop] = override[prop];
					}
				});
		}

		return tableColumnInfo;
	}

	/**
	 * Column initialisation
	 */
	private initTableColumnInfoAndTableColumnSettings() {
		// Create tableColumnInfo using defaultColumnInfo and tableColumns (user properties)
		this.tableColumnInfo = this.initTableColumnInfoFromTableColumns();

		Logger.log('Setting table filter options');
		this.setTableFilterOptions(this.tableColumnInfo);

		// Create tableColumnSetting
		Logger.log('Setting table column settings');
		this.tableColumnSetting = [];

		if (this.contextMenu) {
			// Add context menu button
			this.tableColumnSetting.push({
				title: '',
				data: '__contextMenu',
				orderable: false,
				className: 'text-center a-table-context-icon',
			});
		}
		this.tableColumnInfo
			.map((columnInfo) => {
				columnInfo.isCodeListLoadedWithData =
					this.isCodeListLoadedWithData(columnInfo.name);
				const columnOrderable = this.sortable && !columnInfo.unsortable;

				return {
					title: columnInfo.label,
					data: columnInfo.name,
					orderable: columnOrderable,
					className: this.renderColumnClassName(
						columnInfo,
						columnOrderable,
					),
					width: columnInfo.width,
					visible: !AcurityTable.isHiddenIn(
						columnInfo,
						HiddenTarget.TABLE,
					),
					render: (
						fieldValue: string | CodeAndDescription,
						dataType: 'display',
						row: object,
					) =>
						this.renderColumn(
							fieldValue,
							dataType,
							row,
							columnInfo,
						),
				} as DataTables.ColumnSettings;
			})
			.forEach((column) => this.tableColumnSetting.push(column));
		this.tableColumnOrder = this.tableColumnSetting.map((column) =>
			this.tableColumnSetting.indexOf(column),
		);
	}

	private setTableFilterOptions(tableColumnInfo: TableColumnDefinition[]) {
		tableColumnInfo
			.filter((column) => this.isColumnVisibleInFilter(column))
			.forEach((columnInfo) => {
				// Add filter options to column definitions
				if (columnInfo.filter) {
					const filterCopy: FilterModel = JSON.parse(
						JSON.stringify(columnInfo.filter),
					);
					columnInfo.filter = {
						...createFilterModel(),
						...filterCopy,
					};
				} else {
					columnInfo.filter = createFilterModel();
				}

				if (
					this.customFilterOptions &&
					this.customFilterOptions[columnInfo.name]
				) {
					columnInfo.filter.filterOptions =
						this.customFilterOptions[columnInfo.name];
				} else {
					columnInfo.filter.filterOptions =
						getFilterOptionsForDataType(columnInfo.dataType);
				}
			});
	}

	/**
	 * HTML to be added for the context menu button
	 */
	private getContextMenuHTML(rowData: AcurityTableRecord): string {
		const menuEnabled = this.isContextMenuEnabledForRow(rowData);
		return `
			<button
				type="button"
				class="i-btn btn ${this.contextMenuBtnCls}"
				${menuEnabled ? '' : 'disabled'}
			>
				<i class="fas fa-ellipsis-v" aria-hidden="true"></i>
				<span class="sr-only">
					${this.i18n.tr('acurity-table.toggle-contextual-menu')}
				</span>
			</button>
		`;
	}

	/**
	 * Update the datatable's `pageLength`
	 * @param length {number} length
	 */
	private calculatePageLengthForFillTable(): void {
		if (!this.fillTable) {
			return;
		}
		const pageLength = this.calculatedTablePageLength();
		if (this.pageLength === pageLength) {
			Logger.log(
				`No update needed. Page length is already "${pageLength}"`,
			);
			return;
		}
		Logger.log(
			`#${this.getTableReference().attr(
				'id',
			)}: Updating page length to "${pageLength}"`,
		);
		this.pageLength = pageLength;
	}

	private tableContainerHeight(): number {
		const tableEl = this.element.querySelector('table') as HTMLElement;
		if (!tableEl) {
			Logger.warning('Unable to retrieve <table> element');
			return 0;
		}
		const tableParentEl = tableEl.parentElement;
		if (!tableParentEl) {
			Logger.warning('Unable to retrieve table parent element');
			return 0;
		}
		const height = tableParentEl.clientHeight;
		return height;
	}

	/**
	 * Gets the available height on the datatable's body ()
	 */
	private tableBodyHeight(): number {
		if (!this.jqueryTableReference) {
			throw new Error(
				'No jqueryTableReference set. Cannot call this function before attached().',
			);
		}
		if (this.jqueryTableReference.children().length === 0) {
			return 0;
		}

		// Get table head height
		let theadHeight = 0;
		{
			const rowEl = this.tableEl.querySelector<HTMLElement>('thead');
			if (rowEl) {
				theadHeight = rowEl.offsetHeight;
			}
		}
		if (theadHeight === 0) {
			Logger.warning(
				'Unable to retrieve row height from <table> <thead> element.',
			);
			return 0;
		}

		// Get horizontal scrollbar height (if it exists)
		let horizontalScrollbarHeight = 0;
		{
			const psRailXElem = this.element.querySelector<HTMLElement>(
				'.a-datatable__table.ps .ps__rail-x',
			);
			if (psRailXElem) {
				horizontalScrollbarHeight = psRailXElem.offsetHeight;
			}
		}

		const tableBodyHeight =
			this.tableContainerHeight() -
			theadHeight -
			horizontalScrollbarHeight;
		// Logger.log('TableHeight', tableBodyHeight, ' = ', this.tableContainerHeight(), '-', theadHeight, '-', horizontalScrollbarHeight);
		return tableBodyHeight;
	}

	/**
	 * Get a calculated pageLength for the dataTable
	 */
	private calculatedTablePageLength(): number {
		const lastRowBottomBorderSize = 1;
		const rowContentHeight =
			this.tableBodyHeight() - lastRowBottomBorderSize;
		const r = Math.floor(rowContentHeight / this.rowHeight);
		if (r < 1) {
			// NOTE: Jake: 2019-08-19 - WEBUI-2197
			// If the window height is really tiny, always render at least
			// 1 record in the table.
			return 1;
		}
		return r;
	}

	private createDataTablesRequestColumn(config: {
		data: string;
	}): DataTablesRequestColumn {
		return {
			data: config.data,
			orderable: false,
			search: { value: '', regex: false },
			searchable: false,
		} as DataTablesRequestColumn;
	}
}
